import type { TPlayerRegistrationResponse } from '@lucimark/shared/database/players-directory-schema'
import type { TCtaRegistryEntry } from '@lucimark/shared/models/cta'
import type { IPositionMeasurement } from '@lucimark/shared/models/geolocation'
import type { IAudienceHeartbeatAggregate, IHardwareCapacitySnapshot } from '@lucimark/shared/models/playback-heartbeat'
import type { TRemoteOverlayUi } from '@lucimark/shared/models/player-overlay-ui'
import type {
  IPlaybackScheduleRoot,
  IPlayerRegistrationPayload,
  IScheduleRootAssignment,
  ITenantInfo
} from '@lucimark/shared/models/player-registration-payload'
import type { IWebOtaUpdateEventPayload } from '@lucimark/shared/models/player-update'
import type { TPlayerWeeklyAvailabilitySchedule } from '@lucimark/shared/models/player-weekly-availability'
import type { TWebOtaApplyTiming } from '@lucimark/shared/models/tenant'
import type { App } from 'vue'
import type { IAudienceAnalyticsLiveStats } from '@/helpers/audienceAnalyticsMotionPipeline'
import type { IPlaybackTimerStuckStatus } from '@/helpers/detectPlaybackTimerStuck'
import type { TRegistrationStatusPlayerGroupDisplay } from '@/helpers/registrationPlayerGroupDisplay'
import type { IConnectionStatus } from '@/platform'
import type { IPlaybackTiming } from '@/services/PlaybackScheduler'
import type { TScreenOrientation, TScreenRotation, TScreenSize } from '@/services/ScreenService'
import type { IQueueFrame } from '@/types'
import { Temporal } from '@js-temporal/polyfill'
import {
  isTenantRegistrationDropWebSocketClose
} from '@lucimark/shared/constants/player-websocket'
import { aspectRatioFromViewportDimensions } from '@lucimark/shared/helpers/aspectRatioFromPlaylistFrameLayouts'
import { combineRegistrationScheduleRootsForPlayback } from '@lucimark/shared/helpers/combineRegistrationScheduleRootsForPlayback'
import { GEOHASH_PLAYER_HEARTBEAT_PRECISION, latLngToGeohash } from '@lucimark/shared/helpers/geohash'
import { resolvePlayerEsnDrift } from '@lucimark/shared/helpers/playerEsnDrift'
import { isPlayerPositionMeasurementStale } from '@lucimark/shared/helpers/playerPositionStaleness'
import { getPlaybackScheduleMaterializationIdentity, isScheduleRootAssignment } from '@lucimark/shared/helpers/playbackScheduleMaterializationIdentity'
import {
  operatorAssignedPlayerMasterPlaylistUrn,
  playlistUrnIsPlayerMasterPlaylist
} from '@lucimark/shared/helpers/playerMasterPlaylist'
import { resolvePlayerGlassState } from '@lucimark/shared/helpers/playerGlassState'
import { shouldExcludeFramesWithVideoOnCapacity } from '@lucimark/shared/helpers/playerLowCapacityPolicy'
import { buildPlaybackAspectTelemetrySample } from '@lucimark/shared/helpers/playbackAspectTelemetry'
import { effectiveViewportDimensionsForAspect } from '@lucimark/shared/helpers/playerScreenAspectRatio'
import { shouldSendAudienceAggregateOnPlayerHeartbeat } from '@lucimark/shared/models/audienceHeartbeatPolicy'
import { buildPlaybackTimerStuckHeartbeatFields } from '@lucimark/shared/models/playback-heartbeat'
import { buildPlayerDiagnosticsHeartbeatFields } from '@lucimark/shared/models/player-diagnostics'
import {
  delayMsForNextWeeklyAvailabilityBoundaryPoll,
  isLocalWallClockInstantWithinPlayerWeeklyAvailability,
  msUntilNextPlayerWeeklyAvailabilityTransitionLocalWallClock,
  parsePlayerWeeklyAvailabilitySchedule
} from '@lucimark/shared/models/player-weekly-availability'
import debounce from 'lodash.debounce'
import { computed, reactive, watch } from 'vue'
import {
  MEDIA_LOAD_CHROME_DELAY_MS,
  MODE,
  PAIRING_REGISTRATION_REFETCH_INTERVAL_MS,
  PLAYBACK_HEARTBEAT_INTERVAL_MS,
  POST_PLAYLIST_LOAD_HOLD_MS,
  POWER_SOURCE_HEARTBEAT_DEBOUNCE_MS,
  REGISTRATION_REFETCH_INTERVAL_MS,
  SCREEN_LAYOUT_HEARTBEAT_DEBOUNCE_MS,
  VERSION
} from '@/constants'
import {
  isKioskServicePromptOpen,
  refreshKioskPromptExpectedPin,
  syncAndroidKioskLockPolicy
} from '@/composables/useKioskServiceUnlock'
import {
  armLocalStatusOverlayAutoHide,
  clearLocalStatusOverlayAutoHide
} from '@/helpers/localStatusOverlayAutoHide'
import {
  armPlaybackHeartbeatCadence,
  type PlaybackHeartbeatCadenceHandle
} from '@/helpers/playbackHeartbeatJitter'
import {
  armPlaybackHeartbeatWatchdog,
  clearPlaybackHeartbeatWatchdog,
  notePlaybackHeartbeatSuccess
} from '@/helpers/playbackHeartbeatWatchdog'
import {
  expireRemoteOverlaysIfNeeded,
  filterRemoteOverlayDirective,
  noteRemoteOverlayWsSet
} from '@/helpers/playerDiagnostics/remoteOverlaySessionLatch'
import { addPlayerApkOtaSentryBreadcrumb } from '@/helpers/addPlayerApkOtaSentryBreadcrumb'
import { addPlayerLowMemorySentryBreadcrumb } from '@/helpers/addPlayerLowMemorySentryBreadcrumb'
import { applyPlayerSentryIdentity } from '@/helpers/applyPlayerSentryIdentity'
import { isAndroidPlayStoreDistributionResolved } from '@/helpers/androidDistribution'
import {
  probeAccessibilityServiceEnabled,
  probeDeviceOwnerApp
} from '@/helpers/androidAccessibility'
import { fetchAndroidAmbientLightStatus, startAndroidAmbientLight, stopAndroidAmbientLight } from '@/helpers/androidAmbientLight'
import { applyAndroidAutomaticBrightness, applyAndroidBrightness, fetchAndroidBrightnessStatus } from '@/helpers/androidBrightness'
import { fetchAndroidPowerStatus, listenAndroidPowerSourceChange } from '@/helpers/androidPowerStatus'
import { createBrightnessAutomation } from '@/helpers/brightnessAutomation'
import { recordAndroidProcessHeartbeat } from '@/helpers/androidProcessSelfHeal'
import { applyAndroidScreenOrientation as invokeApplyAndroidScreenOrientation } from '@/helpers/androidScreenOrientation'
import {
  createAudienceAnalyticsMotionPipeline

} from '@/helpers/audienceAnalyticsMotionPipeline'
import {
  peekAudienceHeartbeatAggregatePending,
  setAudienceAggregatePendingListener,
  takeAudienceHeartbeatAggregateForNextHeartbeat
} from '@/helpers/audienceHeartbeatAttachment'
import { createAudienceWebRtcPreviewSession } from '@/helpers/audienceWebRtcPreviewSession'
import { collectScheduleRootAssetUrns } from '@/helpers/collectScheduleRootAssetUrns'
import {
  playbackScheduleHasGeoFilter,
  resolvePlaybackGeoPointForSchedule
} from '@/helpers/playbackAssignmentGeo'
import type { IAndroidPowerStatus } from '@/helpers/parseAndroidPowerStatus'
import type { TViewportRotationMode } from '@/helpers/cssEffectiveScreenRotation'
import {
  cssEffectiveRotationForWindow,
  viewportAspectMatchesLogicalRotation
} from '@/helpers/cssEffectiveScreenRotation'
import { applyDevHardwareCapacityTierOverride } from '@/helpers/devHardwareCapacityTierOverride'
import { observePlaybackAspectTelemetry } from '@/helpers/observePlaybackAspectTelemetry'
import { sampleVisibleMediaRectForAspectTelemetry } from '@/helpers/sampleVisibleMediaRectForAspectTelemetry'
import { registerE2eRegistrationStatusRefetch } from '@/helpers/e2ePlaybackProbe'
import {
  getCachedAudienceVideoInputDevicesForHeartbeat,
  invalidateAudienceVideoInputDevicesCache
} from '@/helpers/enumerateAudienceVideoInputDevices'
import { fetchPairingRegistrationWithDedupe } from '@/helpers/fetchPairingRegistrationWithDedupe'
import getCurrentURLS, { getApiURL } from '@/helpers/getCurrentURLS'
import { getLocalAsUtcInstant } from '@/helpers/getLocalAsUtcInstant'
import { createInFlightCoalesce } from '@/helpers/inFlightCoalesce'
import {
  collectPendingPlaylistHeadAssetUrns,
  collectPendingPlaylistPreloadAssetUrns
} from '@/helpers/collectPendingPlaylistHeadAssetUrns'
import { hybridPlaybackFileStat } from '@/helpers/androidHybridPlayback'
import {
  DEFERRED_REGISTRATION_PLAYBACK_MAX_MS,
  DEFERRED_REGISTRATION_PLAYBACK_READY_MAX_REMAINING_MS,
  deferredRegistrationForceDelayUntilDeadlineMs,
  onAirFrameRemainingMs,
  onAirQueueFrameExpectsVideo
} from '@/helpers/deferredRegistrationPlaybackFlush'
import {
  FEED_MATERIALIZATION_DESYNC_HEAL_INTERVAL_MS,
  isFeedAheadOfMaterialization,
  shouldSkipFeedAheadRematerializeWhileSoftDefer
} from '@/helpers/feedAheadOfMaterialization'
import { isRegistrationAheadOfPlayback, isRegistrationScheduleAheadOfOnAir } from '@/helpers/registrationAheadOfPlayback'
import { getQueueSequenceHash } from '@/helpers/queueHash'
import {
  pendingPlaylistHeadReadyProgress01,
  pendingPlaylistUrnReadyWithoutBinary
} from '@/helpers/pendingPlaylistHeadReady'
import {
  resolvePendingPlaylistSwapFlushReason,
  resolvePendingPlaylistSwapPhaseUpdate,
  shouldApplyDeferredRegistrationPlaybackFlush,
  shouldRestartPendingPlaylistPrefetch,
  type TPendingPlaylistSwapFlushReason,
  type TPendingPlaylistSwapPhase
} from '@/helpers/pendingPlaylistSwapFlushGate'
import { resolvePendingPlaylistSwapTitle } from '@/helpers/pendingPlaylistSwapTitle'
import { shouldClearContentChangeReceivingCue } from '@/helpers/shouldClearContentChangeReceivingCue'
import { getWebBundleVersion } from '@/helpers/getWebBundleVersion'
import { getWebHardwareCapacity } from '@/helpers/getWebHardwareCapacity'
import { normalizeWebOtaVersionToken } from '@/helpers/normalizeWebOtaVersionToken'
import { peekWebPairingRegistrationCodeFromLocalStorage } from '@/helpers/peekWebPairingRegistrationCode'
import {
  initPlayerBootTelemetry,
  markPlayerBootTelemetrySent,
  markPlayerCleanShutdown,
  takePlayerBootTelemetryHeartbeatBody
} from '@/helpers/playerBootTelemetry'
import { handlePlayerDiagnosticCommand } from '@/helpers/playerDiagnostics/handlePlayerDiagnosticCommand'
import { handlePlayerRemoteCommand } from '@/helpers/playerRemote/handlePlayerRemoteCommand'
import {
  buildPlayerDiagnosticsSnapshot,
  initPlayerDiagnosticsWorker
} from '@/helpers/playerDiagnostics/playerDiagnosticService'
import { setPlayerGlassDiagnosticsSample } from '@/helpers/playerDiagnostics/playerGlassDiagnosticsSample'
import { registerPlaylistSwapDiagnosticsProvider } from '@/helpers/playerDiagnostics/playlistSwapDiagnosticsSample'
import { isPlayerDiagnosticsSamplingEnabled } from '@/helpers/playerDiagnostics/playerDiagnosticsKillSwitch'
import { syncPlayerLivePreviewUploadCadence } from '@/helpers/playerLivePreviewUpload'
import {
  PLAYER_AUTH_SOFT_RETRY_CODES,
  recordPlayerAuthAmbiguousFailure,
  registrationBackupDispositionForPersistedClear,
  resetPlayerAuthAmbiguousFailureStreak,
  shouldClearPersistedRegistration,
  tryAcquireAmbiguousClearCooldown
} from '@/helpers/playerRegistrationAuthPolicy'
import { createRegistrationStatusPushRefetchCoalescer } from '@/helpers/registrationStatusPushRefetchCoalesce'
import { reportRegistrationClearedToSentry } from '@/helpers/reportRegistrationClearedToSentry'
import { getResolvedPlayerAppVersion, isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'
import { shouldHydrateCachedPairingRegistration } from '@/helpers/shouldHydrateCachedPairingRegistration'
import { createRegistrationClearDirectoryGate } from '@/helpers/tryAbortAmbiguousClearViaDirectory'
import { performWatchdogRecoveryReload } from '@/helpers/watchdogRecoveryReload'
import {
  PLAYBACK_HEARTBEAT_DEVICE_ID_TIMEOUT_MS,
  PLAYBACK_HEARTBEAT_STORAGE_ESTIMATE_TIMEOUT_MS,
  TimeoutError,
  withTimeout
} from '@/helpers/withTimeout'
import { resetWebOtaSessionGate, shouldAllowWebOtaAutoApplyNow } from '@/helpers/webOtaApplySessionGate'
import {
  clearWebOtaBrowserReloadSession,
  getWebOtaBrowserReloadAttempts,
  incrementWebOtaBrowserReloadAttempts,
  rollbackWebOtaBrowserReloadAttempt,
  WEB_OTA_BROWSER_MAX_RELOAD_ATTEMPTS
} from '@/helpers/webOtaBrowserReloadSession'
import { clearWidgetSwCaches, prepareBrowserWebOtaReload, resetServiceWorkerForNewWebBundle } from '@/helpers/widgetSpaLoader'
import { exitWebFullscreen, getFullscreenElement } from '@/helpers/webFullscreen'
import { fetchWebPowerStatus, listenWebPowerSourceChange } from '@/helpers/webPowerStatus'
import { getPlatform, getPlatformInstance, getPlayerPlatformHeader } from '@/platform'
import { APIClientService, ApiClientServiceError, ApiClientServiceUnauthorizedError } from '@/services/APIClientService'
import AssetsService, { AssetsServiceEvent } from '@/services/AssetsService'
import { AuthenticationService, AuthenticationServiceEvent } from '@/services/AuthenticationService'
import { createHeartbeatQueueFlusher } from '@/helpers/flushHeartbeatQueueSingleFlight'
import DBService from '@/services/DBService'
import GeolocationService, { GeolocationServiceEvent } from '@/services/GeolocationService'
import Kalman from '@/services/kalman'
import { getLoggerService } from '@/services/logger'
import NetworkStatusService, { NetworkStatusEvent } from '@/services/NetworkStatusService'
import PlaybackScheduleMaterializationService, {
  PlaybackScheduleMaterializationEvent,
  PlaybackScheduleMaterializationState
} from '@/services/PlaybackScheduleMaterializationService'
import { PlayerUpdaterService, PlayerUpdateServiceEvent } from '@/services/PlayerUpdaterService'
import { PoolingService, PoolingServiceEvent } from '@/services/PoolingService'
import {
  RegistrationStatusService,
  RegistrationStatusServiceEvent
} from '@/services/RegistrationStatusService'
import { ScreenEvent, ScreenService } from '@/services/ScreenService'
import { SentryService } from '@/services/SentryService'
import { WatchdogService } from '@/services/WatchdogService'
import { WebSocketClientState } from '@/services/WebSocketClient'
import WebSocketMessageProcessor from '@/services/WebSocketMessageProcessor'
import WebSocketService, { WebSocketServiceEvent } from '@/services/WebSocketService'
import { getSharedWorkersService } from '@/services/WorkersService'
import { campaignHash, canonicalStringify } from '@/workers/registrationStatusWorkerHash'

export interface IPlayerState {
  baseURL: string
  mode: string
  playerId: string
  /**
   * First 8 chars of ANDROID_ID (heartbeat / Studio ESN). Null until resolved on Android;
   * unused on web.
   */
  androidIdPrefix: string | null
  version: string
  /**
   * Native APK OTA UI state. `status`: `available` | `downloading` | `complete` (installer launched or immediate path) |
   * `complete_pending_restart` (APK cached; restart or policy change applies) | `failed`.
   */
  update: { version: string, download?: number, status: string, error?: string } | null
  platform: string
  tenant: ITenantInfo | null
  playerGroup: TRegistrationStatusPlayerGroupDisplay | null
  /** From registration `assignedMasterPlaylistUrn`; drives master timeBlock bypass in segment resolution. */
  assignedMasterPlaylistUrn: string | null
  playerTitle: string
  playerDescription: string | null
  /** Active playback schedule from registration (legacy campaign assignment or playlist-only root). */
  campaign: IPlaybackScheduleRoot | null
  nextCampaign: IPlaybackScheduleRoot | null
  /** Registration `schedule` array (playback roots); updated with registration-status. */
  registrationScheduleRoots: IScheduleRootAssignment[]
  networkStatus: IConnectionStatus
  geolocationRunning: boolean
  geolocationPosition: IPositionMeasurement | null
  geolocationPositionDMS: string | null
  geolocationError: GeolocationPositionError | null
  geolocationPositionUpdates: number
  appURL: string
  webSocketServiceState: WebSocketClientState
  poolingServiceRunning: boolean
  registrationCode: string | null
  /** True after tenant revoke until pairing GET /registration returns a new code (shows QR shell immediately). */
  pairingAfterUnregisterPending: boolean
  /** Set when initial /registration fetch fails before we have a code or tenant; cleared on successful REGISTRATION. */
  registrationBootstrapError: string | null
  queue: IQueueFrame[]
  /** True briefly after queue goes from empty to non-empty so idle can stay on the download screen before VBuffer mounts. */
  postPlaylistLoadHoldActive: boolean
  /**
   * True once VBuffer has applied a non-empty playback surface at least once this registration session.
   * Used to hold `#app-loading` until real pixels paint (or the boot cover max timeout).
   */
  firstPlaybackFramePainted: boolean
  loadingCampaign: boolean
  /** Set when campaign asset load fails; cleared on READY/IDLE or campaign cleared. Shown on idle UI when queue is empty. */
  campaignLoadError: string | null
  /** Editorial frames omitted until their image/video binary is cached. */
  skippedUncachedFrameCount: number
  downloadProgress: number
  showDebugModal: boolean
  isFullScreen: boolean
  mouseMoving: boolean
  screenRotation: TScreenRotation
  /**
   * How the current viewport realizes logical screenRotation. `native` = Activity orientation stuck
   * (skip CSS transforms). Default / web / verify-fail = `css`.
   */
  viewportRotationMode: TViewportRotationMode
  /** Last Lucimark angle native Activity stuck at; CSS uses the remainder vs {@link screenRotation}. */
  nativeAppliedRotation: TScreenRotation | null
  screenOrientation: TScreenOrientation
  screenSize: TScreenSize
  rtt: number
  /** Offset (ms) to add to local time to approximate server time; from registration-status serverTimeMs. */
  serverClockOffsetMs: number
  /** When true, show the timers overlay (playlist/frame running times). Toggled from CurrentState. */
  showTimersOverlay: boolean
  /** Current playback timing from queue worker; updated when showTimersOverlay is true. */
  playbackTiming: IPlaybackTiming | null
  /**
   * `performance.now()` when {@link playbackTiming} was last stamped from the worker.
   * Timers overlay extrapolates Campaign/Frame lines between samples so display seconds do not skip.
   */
  playbackTimingSampleAtPerfMs: number | null
  /** Timers overlay stuck heuristic; null when overlay off or idle. */
  playbackTimerStuck: IPlaybackTimerStuckStatus | null
  /** Ms until current widget was made visible (from promote start). null when current frame has no widget. Used by Timers overlay. */
  widgetRevealMs: number | null
  /** True when the widget was promoted after ready timeout (not ready in time). */
  widgetRevealTimedOut: boolean
  /** Corner index (0–3) for status panel position; cycles every 60s. Timers overlay uses opposite corner. */
  currentStateCorner: number
  /** Number of widget/iframe assets still loading (not yet iframeLoaded). Used for debug status. */
  widgetsLoadingCount: number
  /** Bumped when campaign assets change (e.g. widget defaultConfig) so VBuffer can refresh asset index even if queue reference is unchanged. */
  campaignAssetsVersion: number
  /**
   * While true, VBuffer skips queue-watch worker pushes; {@link applyCampaignRegistrationPayloadToPlayback}
   * runs one {@link registerRegistrationPlaybackResyncHandler} resync after materialization finishes.
   */
  registrationPlaybackResyncPending: boolean
  /**
   * Soft-deferred master playlist assign: on-air Feed stays put while the next program prefetches.
   * Cleared when rematch finishes (or defer is cancelled). Drives Feed-row content-change spinner.
   */
  pendingPlaylistSwap: null | {
    title: string
    version?: number
    progress01: number
    phase: TPendingPlaylistSwapPhase
    /** Absolute wall time for status-overlay ETA (flush / cutover expectation). */
    etaAtMs?: number | null
  }
  /**
   * True from WS `registration-status-update` until soft-defer handoff, apply settle, or no-op —
   * drives CurrentState Feed-row spinner (receiving) before prepare/cutover.
   */
  registrationStatusRefreshPending: boolean
  /** Timestamp (ms) when a campaign update was last received; used to show a discrete notification for field engineers. 0 = do not show. */
  campaignUpdateReceivedAt: number
  /** Timestamp (ms) when registration-status was last received while unregistered; used to show a small top-left blip. 0 = do not show. */
  registrationStatusReceivedAt: number
  /**
   * Whether the active schedule came from a live registration-status fetch (`live`) or an IndexedDB
   * offline snapshot (`cached`). Cleared back to `live` on a successful server response or when
   * registration is cleared. Used by CurrentState / Timers overlay for field diagnostics.
   */
  registrationConfigurationSource: 'live' | 'cached'
  /** Wall time when the IndexedDB registration snapshot was last saved; kept when applying cached config so age stays accurate. */
  registrationSnapshotSavedAtMs: number | null
  /** Device capacity tier from tauri-plugin-device-capacity (low | mid | high). null until first read. Used for tier-based rendering (e.g. disable video on low). */
  capacityTier: 'low' | 'mid' | 'high' | null
  /** True when watchLowMemory() reported low memory; player may flush PoP queue and reduce load. */
  lowMemoryMode: boolean
  /** Fullscreen “show me” overlay from dashboard (player name), driven by WebSocket `show-me`. */
  showMeOverlay: { visible: boolean, title: string }
  /** Server-offered web OTA bundle version (registration-status or WS handshake); for CurrentState display. */
  offeredWebOtaVersion: string | null
  /** From registration-status payload; server opt-in for audience analytics (CV aggregates). */
  audienceAnalyticsEnabled: boolean
  /** Preferred audience camera device id from server; null = default enumeration on device. */
  audienceVideoInputDeviceId: string | null
  /**
   * Audience motion pipeline: partial-window motion/presence preview for Current State (web or Android WebView).
   * `null` when the pipeline is not running (unsupported platform, tenant off, low tier, etc.).
   */
  audienceAnalyticsLive: IAudienceAnalyticsLiveStats | null
  /** Aggregate waiting for the next playback heartbeat POST (after a window flush). */
  audienceAnalyticsPendingHeartbeat: IAudienceHeartbeatAggregate | null
  /** Last aggregate attached to a heartbeat body (cleared on player reset). */
  audienceAnalyticsLastSentAggregate: IAudienceHeartbeatAggregate | null
  /** Last `utcOffsetMinutes` sent on playback heartbeat (`-Date.getTimezoneOffset()`); null until first send. */
  lastReportedUtcOffsetMinutes: number | null
  /** Parsed registration weekly playback windows (device local wall clock); null or empty blocks = unrestricted. */
  availabilityWeeklySchedule: TPlayerWeeklyAvailabilitySchedule | null
  /** Bumped every second while weekly restriction is configured; drives blackout UI reactivity. */
  weeklyAvailabilityGateEpoch: number
  /** Global portal ad order id from registration-status; sent as heartbeat `orderId` when set. */
  proofOfPlayOrderId: string | null
  /** Tenant CTA templates keyed by id (from registration-status). */
  ctaRegistry: Record<string, TCtaRegistryEntry>
  /** Screen venue for CTA URL interpolation. */
  venueId: string | null
  /** Tenant handle for CTA URL interpolation. */
  tenantHandle: string | null
}

const { apiURL, appURL } = getCurrentURLS()

/** Use `getPlatform()` at call time — never snapshot at module load (runs before `initPlatform()`). */
function supportsAudienceCameraSurface(): boolean {
  const p = getPlatform()
  return p === 'web' || p === 'android'
}

/** First 8 chars of ANDROID_ID for playback-heartbeat (Android); undefined = not read yet, null = unavailable. */
let cachedAndroidIdPrefixForHeartbeat: string | null | undefined
let loggedPlayerEsnDrift = false

async function resolveAndroidIdPrefixForHeartbeat(): Promise<string | undefined> {
  if (getPlatform() !== 'android') {
    return undefined
  }
  if (cachedAndroidIdPrefixForHeartbeat !== undefined) {
    const cached = cachedAndroidIdPrefixForHeartbeat ?? undefined
    if (cached != null && state.androidIdPrefix !== cached) {
      state.androidIdPrefix = cached
    }
    return cached
  }
  try {
    const id = (await withTimeout(
      getPlatformInstance().deviceId(),
      PLAYBACK_HEARTBEAT_DEVICE_ID_TIMEOUT_MS,
      'deviceId'
    )).trim()
    const prefix = id.length >= 8 ? id.slice(0, 8) : id.length > 0 ? id : ''
    cachedAndroidIdPrefixForHeartbeat = prefix.length > 0 ? prefix : null
    if (cachedAndroidIdPrefixForHeartbeat != null) {
      state.androidIdPrefix = cachedAndroidIdPrefixForHeartbeat
    }
    return cachedAndroidIdPrefixForHeartbeat ?? undefined
  }
  catch (err) {
    // Timeout: leave cache unset so the next heartbeat can retry native IPC.
    if (!(err instanceof TimeoutError)) {
      cachedAndroidIdPrefixForHeartbeat = null
    }
    else {
      logger.warn({ err }, 'deviceId timed out; playback heartbeat continues without androidIdPrefix')
    }
    return undefined
  }
}

/** Studio-aligned ESN tags for Sentry (uses `androidIdPrefix` when known). */
function applySentryIdentityFromState(
  extra?: Partial<{
    capacityTier: 'low' | 'mid' | 'high' | null
    tenantHandle: string | null
    lowMemoryMode: boolean
  }>
): void {
  if (!state.playerId) {
    return
  }
  applyPlayerSentryIdentity({
    playerId: state.playerId,
    platform: state.platform || getPlatform(),
    androidIdPrefix: state.androidIdPrefix,
    capacityTier: extra?.capacityTier ?? state.capacityTier,
    tenantHandle: extra?.tenantHandle !== undefined ? extra.tenantHandle : state.tenantHandle,
    lowMemoryMode: extra?.lowMemoryMode ?? state.lowMemoryMode
  })
}

const logger = getLoggerService({ level: 'debug' })
const networkStatus = new NetworkStatusService()
const screen = new ScreenService()
const dbService = new DBService()
const kalman = new Kalman({ coordinateDecimalPlaces: 5 })
const geolocationService = new GeolocationService({ kalman, logger })
const authenticationService = new AuthenticationService({ mode: MODE })
const apiClientService = new APIClientService({
  apiURL: getApiURL,
  authenticationService,
  getAppVersion: getResolvedPlayerAppVersion,
  platform: () => getPlayerPlatformHeader(),
  getWebVersion: getWebBundleVersion
})
const assetsService = new AssetsService({ dbService, apiClientService })
const playerUpdaterService = new PlayerUpdaterService({ authenticationService, mode: MODE, baseURL: getApiURL() })
const sentryService = new SentryService()
const watchdogService = new WatchdogService()
/** Loads queue + assets from the active registration playback schedule root. */
const playbackMaterializationService = new PlaybackScheduleMaterializationService({
  assetsService,
  logger
})
const registrationStatusService = new RegistrationStatusService()

/** Platform-injected fields merged into every widget's config (spread last so user tiers cannot override). */
const platformWidgetConfig = computed<Record<string, unknown>>(() => ({}))

// called ASAP
void playerUpdaterService.init()

/** Legacy keys — cleared on boot; overlay visibility is app-controlled only (no local persistence). */
const LEGACY_SHOW_TIMERS_OVERLAY_KEY = 'LUCIMARK:SHOW_TIMERS_OVERLAY'
const LEGACY_SHOW_STATUS_OVERLAY_KEY = 'LUCIMARK:SHOW_STATUS_OVERLAY'
const OVERLAY_UI_DIRECTIVE_SEQ_KEY = 'LUCIMARK:OVERLAY_UI_DIRECTIVE_SEQ_APPLIED'

function clearLegacyOverlayVisibilityStorage(): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(LEGACY_SHOW_TIMERS_OVERLAY_KEY)
      window.localStorage.removeItem(LEGACY_SHOW_STATUS_OVERLAY_KEY)
    }
  }
  catch {
    // ignore
  }
}

clearLegacyOverlayVisibilityStorage()
const ASSET_CACHE_REPAIR_DIRECTIVE_SEQ_KEY = 'LUCIMARK:ASSET_CACHE_REPAIR_DIRECTIVE_SEQ_APPLIED'

function readPersistedOverlayDirectiveSeq(): number {
  try {
    if (typeof window === 'undefined') {
      return 0
    }
    const v = window.localStorage?.getItem(OVERLAY_UI_DIRECTIVE_SEQ_KEY)
    const n = v != null ? Number.parseInt(v, 10) : 0
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  catch {
    return 0
  }
}

function persistOverlayDirectiveSeq(seq: number): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(OVERLAY_UI_DIRECTIVE_SEQ_KEY, String(seq))
    }
  }
  catch {
    // ignore
  }
}

let lastAppliedOverlayUiDirectiveSeq = readPersistedOverlayDirectiveSeq()

function readPersistedAssetCacheRepairDirectiveSeq(): number {
  try {
    if (typeof window === 'undefined') {
      return 0
    }
    const v = window.localStorage?.getItem(ASSET_CACHE_REPAIR_DIRECTIVE_SEQ_KEY)
    const n = v != null ? Number.parseInt(v, 10) : 0
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  catch {
    return 0
  }
}

function persistAssetCacheRepairDirectiveSeq(seq: number): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(ASSET_CACHE_REPAIR_DIRECTIVE_SEQ_KEY, String(seq))
    }
  }
  catch {
    // ignore
  }
}

let lastAppliedAssetCacheRepairDirectiveSeq = readPersistedAssetCacheRepairDirectiveSeq()

/** Serializes IndexedDB scan + campaign reload so overlapping directives / heartbeats do not run repairs concurrently (S2). */
let assetCacheRepairLock: Promise<void> = Promise.resolve()

/** Cache for hardware capacity from tauri-plugin-device-capacity; refreshed every 5 min. Web has no plugin, so returns null. */
const HARDWARE_CAPACITY_CACHE_TTL_MS = 5 * 60 * 1000
let hardwareCapacityCache: { at: number, data: IHardwareCapacitySnapshot } | null = null

/**
 * Fresh read from native plugin with retry/backoff (`hardwareCapacityPluginFetch.ts`).
 * On repeated failure, returns last cached snapshot if any.
 */
async function getCachedHardwareCapacity(): Promise<IHardwareCapacitySnapshot | null> {
  const withDevOverride = (cap: IHardwareCapacitySnapshot | null) =>
    cap != null ? applyDevHardwareCapacityTierOverride(cap) : null

  if (hardwareCapacityCache != null && Date.now() - hardwareCapacityCache.at < HARDWARE_CAPACITY_CACHE_TTL_MS) {
    return withDevOverride(hardwareCapacityCache.data)
  }
  const { getTauriHardwareCapacityWithRetry } = await import('@/helpers/hardwareCapacityPluginFetch')
  const cap = await getTauriHardwareCapacityWithRetry()
  if (cap != null) {
    hardwareCapacityCache = { at: Date.now(), data: cap }
    return withDevOverride(cap)
  }
  return withDevOverride(hardwareCapacityCache?.data ?? null)
}

const state = reactive<IPlayerState>({
  baseURL: apiURL,
  mode: MODE,
  playerId: '',
  androidIdPrefix: null,
  version: VERSION,
  update: null,
  /** Synced from `getPlatform()` in `init()` after `initPlatform()` — must not snapshot at module load. */
  platform: 'web',
  tenant: null,
  playerGroup: null,
  assignedMasterPlaylistUrn: null,
  playerTitle: '',
  playerDescription: null,
  campaign: null,
  nextCampaign: null,
  registrationScheduleRoots: [],
  networkStatus: networkStatus.connectionStatus,
  geolocationRunning: false,
  geolocationError: null,
  geolocationPosition: null,
  geolocationPositionDMS: null,
  geolocationPositionUpdates: 0,
  appURL,
  webSocketServiceState: WebSocketClientState.DISCONNECTED,
  poolingServiceRunning: false,
  registrationCode: null,
  pairingAfterUnregisterPending: false,
  registrationBootstrapError: null,
  queue: [],
  postPlaylistLoadHoldActive: false,
  firstPlaybackFramePainted: false,
  loadingCampaign: false,
  campaignLoadError: null,
  skippedUncachedFrameCount: 0,
  downloadProgress: Number.NaN,
  showDebugModal: false,
  isFullScreen: false,
  mouseMoving: false,
  screenRotation: screen.rotation,
  viewportRotationMode: 'css' as TViewportRotationMode,
  nativeAppliedRotation: null as TScreenRotation | null,
  screenOrientation: screen.orientation,
  screenSize: { width: 0, height: 0 },
  rtt: 0,
  serverClockOffsetMs: 0,
  showTimersOverlay: false,
  playbackTiming: null,
  playbackTimingSampleAtPerfMs: null,
  playbackTimerStuck: null,
  widgetRevealMs: null,
  widgetRevealTimedOut: false,
  currentStateCorner: 0,
  widgetsLoadingCount: 0,
  campaignAssetsVersion: 0,
  registrationPlaybackResyncPending: false,
  pendingPlaylistSwap: null,
  registrationStatusRefreshPending: false,
  campaignUpdateReceivedAt: 0,
  registrationStatusReceivedAt: 0,
  registrationConfigurationSource: 'live',
  registrationSnapshotSavedAtMs: null,
  capacityTier: null as 'low' | 'mid' | 'high' | null,
  lowMemoryMode: false,
  showMeOverlay: { visible: false, title: '' },
  offeredWebOtaVersion: null,
  audienceAnalyticsEnabled: false,
  audienceVideoInputDeviceId: null,
  audienceAnalyticsLive: null,
  audienceAnalyticsPendingHeartbeat: null,
  audienceAnalyticsLastSentAggregate: null,
  lastReportedUtcOffsetMinutes: null,
  /** Parsed registration weekly playback windows (device local wall clock); null or empty blocks = unrestricted. */
  availabilityWeeklySchedule: null,
  weeklyAvailabilityGateEpoch: 0,
  proofOfPlayOrderId: null,
  ctaRegistry: {},
  venueId: null,
  tenantHandle: null
})

function liveCssEffectiveRotation(): TScreenRotation {
  const { width, height } = state.screenSize
  return cssEffectiveRotationForWindow(
    state.viewportRotationMode,
    state.screenRotation,
    state.nativeAppliedRotation,
    width,
    height
  )
}

let applyAndroidOrientationGeneration = 0

/**
 * Android: try native Activity orientation; otherwise keep CSS. Generation-guarded against rapid
 * rotates. Leaves {@link IPlayerState.nativeAppliedRotation} on the last confirmed angle so CSS
 * can cover the remainder while a new native apply is in flight.
 */
function applyScreenOrientationToNative(rotation: TScreenRotation) {
  const gen = ++applyAndroidOrientationGeneration
  if (getPlatform() !== 'android') {
    state.viewportRotationMode = 'css'
    state.nativeAppliedRotation = null
    return
  }
  void invokeApplyAndroidScreenOrientation(rotation).then(({ applied }) => {
    if (gen !== applyAndroidOrientationGeneration) {
      return
    }
    if (applied) {
      state.viewportRotationMode = 'native'
      state.nativeAppliedRotation = rotation
    }
    else {
      state.viewportRotationMode = 'css'
      state.nativeAppliedRotation = null
    }
    syncPlaybackAspectRatioFilter()
  })
}

/**
 * Before first Vue paint: if MainActivity already remapped the window, skip CSS so we do not
 * double-rotate during the plugin verify. TVs / old APKs keep CSS (aspect will not match).
 */
function bootstrapAndroidViewportRotationFromWindow() {
  const logical = state.screenRotation
  const { width, height } = state.screenSize
  if (!viewportAspectMatchesLogicalRotation(logical, width, height)) {
    return
  }
  state.viewportRotationMode = 'native'
  state.nativeAppliedRotation = logical
}

/** Applies tenant “viewport aspect only” and low-capacity video-skip policies to the playback resolver. */
function syncPlaybackAspectRatioFilter(): void {
  const registered = state.tenant != null
  const restrict = registered && state.tenant!.playerRestrictPlaybackToDeviceAspectRatio !== false
  const { width, height } = state.screenSize
  const { width: ew, height: eh } = effectiveViewportDimensionsForAspect(
    width,
    height,
    liveCssEffectiveRotation()
  )
  const viewportAspect = aspectRatioFromViewportDimensions(ew, eh)
  const allowed = restrict ? new Set([viewportAspect]) : null
  const portalBookViewportAspect = registered ? viewportAspect : null
  playbackMaterializationService.setAllowedAspectRatiosForPlayback(allowed)
  playbackMaterializationService.setPortalBookViewportAspectForPlayback(portalBookViewportAspect)
  syncPlaybackExcludeVideoFilter()
  // Cold path (SIZE/ROTATION/registration): always-on vertical-aspect Sentry (playback-safety §3).
  try {
    const sample = buildPlaybackAspectTelemetrySample({
      nowMs: Date.now(),
      innerWidthPx: width,
      innerHeightPx: height,
      screenRotation: liveCssEffectiveRotation(),
      aspectRestrictActive: restrict
    })
    observePlaybackAspectTelemetry(sample, {
      playerId: state.playerId,
      tenantId: state.tenant?.id ?? null
    })
  }
  catch {
    /* never break playback filter sync */
  }
}

/**
 * Heartbeat / frame-transition cold path: viewport + optional on-air layout + painted media box.
 * Frame layout dims come from the caller when known (VBuffer); heartbeat re-samples media only.
 */
function reportPlaybackAspectTelemetrySample(input?: {
  onAirFrameId?: string | null
  onAirFrameLayoutWidthPx?: number | null
  onAirFrameLayoutHeightPx?: number | null
  includeVisibleMediaRect?: boolean
}): void {
  try {
    const registered = state.tenant != null
    const restrict = registered && state.tenant!.playerRestrictPlaybackToDeviceAspectRatio !== false
    const { width, height } = state.screenSize
    const media = input?.includeVisibleMediaRect === true
      ? sampleVisibleMediaRectForAspectTelemetry()
      : null
    const sample = buildPlaybackAspectTelemetrySample({
      nowMs: Date.now(),
      innerWidthPx: width,
      innerHeightPx: height,
      screenRotation: liveCssEffectiveRotation(),
      aspectRestrictActive: restrict,
      onAirFrameId: input?.onAirFrameId,
      onAirFrameLayoutWidthPx: input?.onAirFrameLayoutWidthPx,
      onAirFrameLayoutHeightPx: input?.onAirFrameLayoutHeightPx,
      visibleMediaWidthPx: media?.widthPx,
      visibleMediaHeightPx: media?.heightPx
    })
    observePlaybackAspectTelemetry(sample, {
      playerId: state.playerId,
      tenantId: state.tenant?.id ?? null
    })
  }
  catch {
    /* never break callers */
  }
}

/** Skip frames with video on low-capacity devices unless tenant allows video. */
function syncPlaybackExcludeVideoFilter(): void {
  const exclude = shouldExcludeFramesWithVideoOnCapacity(
    state.capacityTier,
    state.tenant?.playerAllowVideoOnLowCapacity,
    state.tenant != null
  )
  playbackMaterializationService.setExcludeFramesWithVideoForPlayback(exclude)
}

setAudienceAggregatePendingListener((value) => {
  state.audienceAnalyticsPendingHeartbeat = value
})
state.audienceAnalyticsPendingHeartbeat = peekAudienceHeartbeatAggregatePending()

async function syncStateAppVersion(): Promise<string> {
  const v = await getResolvedPlayerAppVersion()
  state.version = v
  return v
}

void syncStateAppVersion()

// First paint: avoid idle "waiting" → QR flicker while async getRegistration() runs (web localStorage).
if (getPlatform() === 'web') {
  const peekCode = peekWebPairingRegistrationCodeFromLocalStorage()
  if (peekCode != null) {
    state.registrationCode = peekCode
  }
}

function setShowTimersOverlay(value: boolean) {
  state.showTimersOverlay = value
}

/**
 * Set status overlay visibility from remote / expiry paths.
 * Always clears the local 5-min auto-hide so Studio session TTL stays authoritative.
 */
function setShowDebugModal(value: boolean) {
  clearLocalStatusOverlayAutoHide()
  state.showDebugModal = value
}

/**
 * Field gesture (triple-tap) / Android back dismiss — not remoted.
 * Opening arms a 5-minute auto-hide so the panel cannot stay on accidentally.
 */
function toggleStatusOverlayFromLocalGesture() {
  if (isKioskServicePromptOpen()) {
    return
  }
  const next = !state.showDebugModal
  clearLocalStatusOverlayAutoHide()
  state.showDebugModal = next
  if (next) {
    armLocalStatusOverlayAutoHide(() => {
      state.showDebugModal = false
    })
  }
}

function applyRemoteOverlayDirectiveFromServer(remoteOverlayUi: TRemoteOverlayUi, overlayUiDirectiveSeq: number) {
  if (!Number.isInteger(overlayUiDirectiveSeq) || overlayUiDirectiveSeq < 0 || overlayUiDirectiveSeq > Number.MAX_SAFE_INTEGER) {
    return
  }
  if (overlayUiDirectiveSeq <= lastAppliedOverlayUiDirectiveSeq) {
    return
  }
  const filtered = filterRemoteOverlayDirective(remoteOverlayUi, overlayUiDirectiveSeq)
  if (filtered.timersVisible !== undefined) {
    setShowTimersOverlay(filtered.timersVisible)
  }
  if (filtered.statusVisible !== undefined) {
    setShowDebugModal(filtered.statusVisible)
  }
  lastAppliedOverlayUiDirectiveSeq = overlayUiDirectiveSeq
  persistOverlayDirectiveSeq(overlayUiDirectiveSeq)
}

/** Registration bootstrap (live or cached): always sync to server mandate — seq dedup is heartbeat-only. */
function applyRemoteOverlayUiFromPayload(payload: IPlayerRegistrationPayload) {
  const r = payload.remoteOverlayUi
  if (r == null) {
    return
  }
  if (r.timersVisible !== undefined) {
    noteRemoteOverlayWsSet('timers', r.timersVisible)
    setShowTimersOverlay(r.timersVisible)
  }
  if (r.statusVisible !== undefined) {
    noteRemoteOverlayWsSet('status', r.statusVisible)
    setShowDebugModal(r.statusVisible)
  }
  const seq = payload.overlayUiDirectiveSeq
  if (seq !== undefined && Number.isInteger(seq) && seq >= 0 && seq <= Number.MAX_SAFE_INTEGER && seq > lastAppliedOverlayUiDirectiveSeq) {
    lastAppliedOverlayUiDirectiveSeq = seq
    persistOverlayDirectiveSeq(seq)
  }
}

function setWidgetRevealTiming(ms: number | null, timedOut = false) {
  state.widgetRevealMs = ms
  state.widgetRevealTimedOut = timedOut
}

/** Zero campaign/frame lines while idle; keep last-frame duration for debugging. */
function resetTimersOverlayForIdlePlayback(): void {
  const lastFrameDurationMs = state.playbackTiming?.lastFrameDurationMs ?? 0
  state.playbackTiming = {
    playlistElapsedMs: 0,
    playlistDurationMs: 0,
    frameElapsedMs: 0,
    frameDurationMs: 0,
    lastFrameDurationMs,
    notifiedIdx: -1
  }
  state.playbackTimingSampleAtPerfMs = performance.now()
  state.playbackTimerStuck = null
}

void authenticationService.getPlayerId().then(async (playerId) => {
  state.playerId = playerId
  await resolveAndroidIdPrefixForHeartbeat()
  applySentryIdentityFromState()
})

/** Refresh searchable Sentry identity tags + Lucimark Player context (best-effort). */
function syncPlayerSentryContext(registrationStatus: unknown = null): void {
  const playerId = state.playerId
  if (!playerId) {
    return
  }
  void sentryService.updateSentryContext({
    playerId,
    registrationStatus,
    platform: state.platform || getPlatform(),
    androidIdPrefix: state.androidIdPrefix,
    tenantHandle: state.tenantHandle,
    capacityTier: state.capacityTier,
    lowMemoryMode: state.lowMemoryMode
  }).catch(() => {})
}

function registrationBootstrapErrorMessage(err: unknown): string {
  if (err instanceof ApiClientServiceUnauthorizedError) {
    return 'Sessão inválida ou jogador removido. O dispositivo vai voltar a pedir registo.'
  }
  if (err instanceof ApiClientServiceError) {
    const m = err.message.match(/status:\s*(-?\d+)/)
    const code = m?.[1] ?? '?'
    return `Não foi possível obter o registo (erro ${code}). Verifique a rede e a URL da API.`
  }
  if (err instanceof Error && err.message) {
    return err.message
  }
  return 'Não foi possível obter o registo. Verifique a ligação e tente novamente.'
}

/** True while re-applying cached pairing via setRegistration — not a fresh server poll; skip registration-status blip. */
let suppressUnregisteredRegistrationStatusBlip = false

async function hydrateCachedPairingRegistrationIfNeeded(reg: TPlayerRegistrationResponse): Promise<void> {
  if (!shouldHydrateCachedPairingRegistration(reg, authenticationService.registrationCode)) { return }
  suppressUnregisteredRegistrationStatusBlip = true
  try {
    await authenticationService.setRegistration(reg)
  }
  finally {
    suppressUnregisteredRegistrationStatusBlip = false
  }
}

// Cached registration from storage does not go through setRegistration on boot, so REGISTRATION may never fire.
void authenticationService.getRegistration().then(async (reg) => {
  if (reg == null) { return }
  if (reg.tenantId != null) {
    const resolved = await syncStateAppVersion()
    if (resolved !== '' && resolved !== reg.version) {
      try {
        await apiClientService.reportVersionUpdate(resolved)
      }
      catch {
        // Non-fatal: REGISTRATION handler or a later heartbeat may retry.
      }
    }
    return
  }
  await hydrateCachedPairingRegistrationIfNeeded(reg)
})

assetsService.on(AssetsServiceEvent.DOWNLOAD_PROGRESS, (progress) => {
  state.downloadProgress = progress
  schedulePendingPlaylistSwapProgressRefresh()
})

screen.on(ScreenEvent.ROTATION, (screenRotation) => {
  state.screenRotation = screenRotation
  applyScreenOrientationToNative(screenRotation)
  syncPlaybackAspectRatioFilter()
})

screen.on(ScreenEvent.ORIENTATION, (screenOrientation) => {
  state.screenOrientation = screenOrientation
})

screen.on(ScreenEvent.SIZE, (screenSize) => {
  state.screenSize = screenSize
  syncPlaybackAspectRatioFilter()
})
// ScreenService emits SIZE in its constructor before this listener exists; copy current dimensions once.
state.screenSize = { ...screen.size }
syncPlaybackAspectRatioFilter()

/**
 * Re-run playback materialization so asset fetches retry without an assignment hash change.
 * In-flight coalesce: pending-commit retries (~1.2s) must not cancel a trailing debounce forever (#1574 / Bugbot).
 */
const playbackAssetReloadCoalesce = createInFlightCoalesce(async () => {
  const assignment = state.campaign ?? playbackMaterializationService.getPlaybackScheduleRootEffective()
  if (assignment === null || state.tenant?.quotaExcess === true || isWeeklyAvailabilitySuppressingPlaybackNow()) {
    return
  }
  try {
    await playbackMaterializationService.setPlaybackSchedule(assignment, { forceAssetReload: true })
  }
  catch (err: unknown) {
    logger.warn({ err }, 'Playback asset recovery reload failed')
  }
})

function requestPlaybackAssetReload(): void {
  playbackAssetReloadCoalesce.request()
}

/** Wake / reconnect: coalesce bursts with trailing debounce; force path uses {@link requestPlaybackAssetReload} directly. */
const debouncedReloadPlaybackAssets = debounce(() => {
  requestPlaybackAssetReload()
}, 2000, { leading: false, trailing: true })

/** Purge/repair IndexedDB binary blobs that cannot play (wrong MIME, error HTML), then reload the active assignment so assets re-fetch. */
async function scanCachedBinaryAssetsAndReloadPlayback() {
  const { purgedUrns, repairedUrns } = await assetsService.scanAndRepairAllCachedBinaryAssets()
  if (purgedUrns.length > 0 || repairedUrns.length > 0) {
    logger.info({ purgedUrns, repairedUrns }, 'scanAndRepairAllCachedBinaryAssets')
  }
  const assignment = state.campaign ?? playbackMaterializationService.getPlaybackScheduleRootEffective()
  if (assignment === null || state.tenant?.quotaExcess === true || isWeeklyAvailabilitySuppressingPlaybackNow()) {
    return { purgedUrns, repairedUrns }
  }
  await playbackMaterializationService.setPlaybackSchedule(assignment, { forceAssetReload: true })
  // Always remount binary media after repair — schedule reload alone keeps stuck `<video>`
  // elements when IndexedDB blobs were not purged (same Blob instance → no assetIndex remount).
  if (cacheRepairMediaRemountHandler != null) {
    try {
      await cacheRepairMediaRemountHandler()
    }
    catch (err) {
      logger.warn({ err }, 'cache repair media remount failed')
    }
  }
  return { purgedUrns, repairedUrns }
}

async function applyAssetCacheRepairDirectiveIfNeeded(seq: number | undefined): Promise<void> {
  if (seq === undefined) {
    return
  }
  if (!Number.isInteger(seq) || seq < 1 || seq > Number.MAX_SAFE_INTEGER) {
    return
  }
  if (seq <= lastAppliedAssetCacheRepairDirectiveSeq) {
    return
  }

  const run = async (): Promise<void> => {
    if (seq <= lastAppliedAssetCacheRepairDirectiveSeq) {
      return
    }
    try {
      await scanCachedBinaryAssetsAndReloadPlayback()
      lastAppliedAssetCacheRepairDirectiveSeq = seq
      persistAssetCacheRepairDirectiveSeq(seq)
    }
    catch (err) {
      logger.warn({ err, seq }, 'asset cache repair directive failed')
    }
  }

  assetCacheRepairLock = assetCacheRepairLock.then(run)
  await assetCacheRepairLock
}

/**
 * Offline heartbeat queue replay (max 100 / 24h in IndexedDB).
 * Single-flight so overlapping online/network events cannot double-send the same items.
 * Items are removed only after a successful ack.
 *
 * TODO(idempotency): playback-heartbeat API does not accept a client idempotency key yet.
 * When it does, stamp each queued body so retries remain safe across process restarts —
 * do not change the server contract from the player alone.
 */
const heartbeatQueueFlusher = createHeartbeatQueueFlusher({
  getHeartbeatQueue: () => dbService.getHeartbeatQueue(),
  async sendHeartbeat(body) {
    const directive = await apiClientService.reportPlaybackHeartbeatWithRetries(
      body as Parameters<typeof apiClientService.reportPlaybackHeartbeat>[0]
    )
    if (directive != null) {
      if (directive.remoteOverlayUi != null && directive.overlayUiDirectiveSeq !== undefined) {
        applyRemoteOverlayDirectiveFromServer(directive.remoteOverlayUi, directive.overlayUiDirectiveSeq)
      }
      await applyAssetCacheRepairDirectiveIfNeeded(directive.assetCacheRepairDirectiveSeq)
    }
    notePlaybackHeartbeatSuccess()
  },
  deleteHeartbeats: ids => dbService.deleteHeartbeats(ids),
  onItemFailed(err, queueItemId) {
    logger.warn({ err, queueItemId }, 'playback heartbeat queue item failed after retries')
  }
})

async function flushHeartbeatQueue() {
  await heartbeatQueueFlusher.flush()
}

/** Bound after {@link audienceWebRtcPreviewSession} is constructed (avoids `no-use-before-define` in heartbeat path). */
let getAudienceWebRtcPreviewActive: () => boolean = () => false

interface TProofOfPlayHeartbeatPayload {
  assetPlays: { assetUrn: string, durationSeconds: number }[]
  playlistUrn?: string
  /**
   * When the property is present (including `null`), overrides `state.proofOfPlayOrderId`
   * so attribution-boundary flushes keep the prior Book order on the previous plays.
   */
  orderId?: string | null
}

/** Sum of `duration` (seconds) over the materialized playback queue; one full loop length. */
function segmentResolvedLoopSecondsFromQueue(): number | undefined {
  const q = state.queue
  if (q.length === 0) {
    return undefined
  }
  let sum = 0
  for (const f of q) {
    const d = f.duration
    if (typeof d === 'number' && Number.isFinite(d) && d > 0) {
      sum += d
    }
  }
  return sum > 0 ? sum : undefined
}

function applyPowerStatusToHeartbeat(
  opts: NonNullable<Parameters<typeof apiClientService.reportPlaybackHeartbeat>[0]>,
  powerStatus: IAndroidPowerStatus | null
): void {
  if (powerStatus == null) {
    return
  }
  opts.powerSource = powerStatus.powerSource
  opts.batteryPresent = powerStatus.batteryPresent
  opts.batteryCharging = powerStatus.batteryCharging
  if (powerStatus.batteryPercent !== undefined) {
    opts.batteryPercent = powerStatus.batteryPercent
  }
}

/** Playback heartbeat: storage, hardware, versions, rotation, overlay visibility, optional proof-of-play. */
async function reportPlaybackHeartbeatWithProofOfPlay(proofOfPlayPayload?: TProofOfPlayHeartbeatPayload) {
  const expiredOverlays = expireRemoteOverlaysIfNeeded(Date.now(), lastAppliedOverlayUiDirectiveSeq)
  if (expiredOverlays.clearTimers) {
    setShowTimersOverlay(false)
  }
  if (expiredOverlays.clearStatus) {
    setShowDebugModal(false)
  }
  const opts: Parameters<typeof apiClientService.reportPlaybackHeartbeat>[0] = {}
  const utcOffsetMinutes = -new Date().getTimezoneOffset()
  opts.utcOffsetMinutes = utcOffsetMinutes
  state.lastReportedUtcOffsetMinutes = utcOffsetMinutes
  try {
    const raw = await withTimeout(
      DBService.getStorageEstimateRaw(),
      PLAYBACK_HEARTBEAT_STORAGE_ESTIMATE_TIMEOUT_MS,
      'storage.estimate'
    )
    if (raw && raw.quota > 0) {
      const pct = Math.round((raw.usage / raw.quota) * 100)
      opts.storageUsedPercent = Math.min(100, Math.max(0, pct))
    }
  }
  catch (err) {
    if (err instanceof TimeoutError) {
      logger.warn({ err }, 'storage.estimate timed out; playback heartbeat continues without storageUsedPercent')
    }
    // ignore – heartbeat still runs without storage
  }
  if (getPlatform() === 'web') {
    const cap = getWebHardwareCapacity()
    if (cap) {
      opts.hardwareCapacity = cap
      if (cap.tier === 'low' || cap.tier === 'mid' || cap.tier === 'high') {
        state.capacityTier = cap.tier
        syncPlaybackExcludeVideoFilter()
        applySentryIdentityFromState({ capacityTier: cap.tier })
      }
    }
    // Chromium-only Battery Status API; omitted (never an error) elsewhere.
    applyPowerStatusToHeartbeat(opts, await fetchWebPowerStatus())
  }
  else {
    try {
      // Per-attempt timeout lives in getTauriHardwareCapacityWithRetry (wedged IPC).
      const cap = await getCachedHardwareCapacity()
      if (cap) {
        opts.hardwareCapacity = cap
        state.capacityTier = cap.tier as 'low' | 'mid' | 'high'
        syncPlaybackExcludeVideoFilter()
        applySentryIdentityFromState()
      }
    }
    catch {
      // ignore – plugin only available in Tauri; timeouts are absorbed in the fetch helper
    }
  }
  const loopSec = segmentResolvedLoopSecondsFromQueue()
  if (loopSec != null) {
    opts.segmentResolvedLoopSeconds = loopSec
  }
  if (proofOfPlayPayload?.assetPlays?.length && !state.lowMemoryMode) {
    opts.assetPlays = proofOfPlayPayload.assetPlays
    if (proofOfPlayPayload.playlistUrn != null && proofOfPlayPayload.playlistUrn !== '') {
      opts.playlistUrn = proofOfPlayPayload.playlistUrn
    }
    if (Object.prototype.hasOwnProperty.call(proofOfPlayPayload, 'orderId')) {
      if (proofOfPlayPayload.orderId != null && proofOfPlayPayload.orderId !== '') {
        opts.orderId = proofOfPlayPayload.orderId
      }
    }
    else if (state.proofOfPlayOrderId != null && state.proofOfPlayOrderId !== '') {
      opts.orderId = state.proofOfPlayOrderId
    }
    const pos = state.geolocationPosition
    if (pos != null && !isPlayerPositionMeasurementStale(pos)) {
      opts.playerGeohash = latLngToGeohash(pos.latitude, pos.longitude, GEOHASH_PLAYER_HEARTBEAT_PRECISION)
    }
  }
  const resolvedAppVersion = await syncStateAppVersion()
  if (resolvedAppVersion !== '') {
    opts.version = resolvedAppVersion
  }
  const webVer = await Promise.resolve(getWebBundleVersion())
  if (webVer != null && webVer !== '') { opts.webVersion = webVer }
  opts.screenRotation = state.screenRotation
  opts.screenOrientation = state.screenOrientation
  const { width: vw, height: vh } = state.screenSize
  if (vw > 0 && vh > 0) {
    opts.screenWidthPx = vw
    opts.screenHeightPx = vh
  }
  // Heartbeat cadence (~1/min): re-sample painted media without relying on RAF diagnostics.
  reportPlaybackAspectTelemetrySample({ includeVisibleMediaRect: true })
  opts.currentStatusOverlayVisible = state.showDebugModal
  opts.timersOverlayVisible = state.showTimersOverlay
  if (state.tenant != null && state.tenant.quotaExcess !== true) {
    opts.weeklyAvailabilityPlaybackSuppressed = isWeeklyAvailabilitySuppressingPlaybackNow()
  }
  Object.assign(opts, buildPlaybackTimerStuckHeartbeatFields({
    showTimersOverlay: state.showTimersOverlay,
    status: state.playbackTimerStuck
  }))
  Object.assign(opts, buildPlayerDiagnosticsHeartbeatFields({
    snapshot: buildPlayerDiagnosticsSnapshot()
  }))
  Object.assign(opts, takePlayerBootTelemetryHeartbeatBody())
  const androidPrefix = await resolveAndroidIdPrefixForHeartbeat()
  if (androidPrefix != null && androidPrefix !== '') {
    opts.androidIdPrefix = androidPrefix
    if (!loggedPlayerEsnDrift) {
      const drift = resolvePlayerEsnDrift({
        playerId: state.playerId,
        platform: getPlatform(),
        androidIdPrefix: androidPrefix
      })
      if (drift.drifted) {
        loggedPlayerEsnDrift = true
        logger.warn(
          {
            registrationPrefix: drift.registrationPrefix,
            devicePrefix: drift.devicePrefix
          },
          'player ESN drift: persisted registration id differs from device ANDROID_ID'
        )
      }
    }
  }
  if (getPlatform() === 'android') {
    const [deviceOwner, accessibilityEnabled, powerStatus, brightnessStatus, ambientLight] = await Promise.all([
      probeDeviceOwnerApp(),
      probeAccessibilityServiceEnabled(),
      fetchAndroidPowerStatus(),
      fetchAndroidBrightnessStatus(),
      fetchAndroidAmbientLightStatus()
    ])
    // Omit on bridge failure so a transient invoke error cannot clear a prior ready report.
    if (typeof deviceOwner === 'boolean') {
      opts.deviceOwner = deviceOwner
    }
    if (typeof accessibilityEnabled === 'boolean') {
      opts.accessibilityEnabled = accessibilityEnabled
    }
    // Same rule: omit on bridge failure so a transient error cannot overwrite the last good sample.
    applyPowerStatusToHeartbeat(opts, powerStatus)
    if (brightnessStatus != null) {
      opts.brightnessMode = brightnessStatus.brightnessMode
      opts.brightnessControl = brightnessStatus.brightnessControl
      opts.brightnessMethod = brightnessStatus.brightnessMethod
      opts.writeSettingsGranted = brightnessStatus.writeSettingsGranted
      if (brightnessStatus.brightnessPercent !== undefined) {
        opts.brightnessPercent = brightnessStatus.brightnessPercent
      }
    }
    if (ambientLight != null) {
      opts.ambientLightSupported = ambientLight.supported
      if (ambientLight.lux !== undefined) {
        opts.ambientLux = Math.round(ambientLight.lux)
      }
    }
  }
  const pendingAudience = peekAudienceHeartbeatAggregatePending()
  if (
    shouldSendAudienceAggregateOnPlayerHeartbeat(
      state.tenant?.audienceAnalytics,
      state.audienceAnalyticsEnabled,
      state.lowMemoryMode,
      pendingAudience ?? undefined
    )
  ) {
    const audienceAggregate = takeAudienceHeartbeatAggregateForNextHeartbeat()
    if (audienceAggregate != null) {
      opts.audienceAggregate = audienceAggregate
      state.audienceAnalyticsLastSentAggregate = { ...audienceAggregate }
    }
  }
  if (
    supportsAudienceCameraSurface()
    && (state.tenant?.audienceWebcamPreview === true || state.tenant?.audienceAnalytics === true)
  ) {
    opts.audienceVideoInputDevices = await getCachedAudienceVideoInputDevicesForHeartbeat({
      isWebRtcPreviewActive: () => getAudienceWebRtcPreviewActive()
    })
  }
  try {
    const directive = await apiClientService.reportPlaybackHeartbeatWithRetries(opts)
    notePlaybackHeartbeatSuccess()
    if (opts.lastBootAt != null) {
      markPlayerBootTelemetrySent()
    }
    if (directive != null) {
      if (directive.remoteOverlayUi != null && directive.overlayUiDirectiveSeq !== undefined) {
        applyRemoteOverlayDirectiveFromServer(directive.remoteOverlayUi, directive.overlayUiDirectiveSeq)
      }
      void applyAssetCacheRepairDirectiveIfNeeded(directive.assetCacheRepairDirectiveSeq)
    }
  }
  catch (err) {
    if (err instanceof ApiClientServiceUnauthorizedError) {
      logger.warn({ err }, 'playback heartbeat unauthorized; not enqueueing offline replay')
      return
    }
    if (err instanceof ApiClientServiceError && err.httpStatus === 400) {
      logger.error({ err }, 'playback heartbeat rejected (bad request); not enqueueing offline replay')
      return
    }
    logger.warn({ err }, 'playback heartbeat failed after retries; enqueueing for offline replay')
    await dbService.enqueueHeartbeat(opts as Record<string, unknown>)
  }
}

/**
 * Viewport / rotation can change faster than {@link PLAYBACK_HEARTBEAT_INTERVAL_MS}; send an extra playback-heartbeat
 * so `screen_width_px` / `screen_height_px` (and rotation) persist for the management app aspect column, without spamming the API.
 */
const debouncedReportScreenLayoutHeartbeat = debounce(
  () => {
    const tenant = state.tenant
    if (tenant == null || tenant.quotaExcess === true) {
      return
    }
    const { width, height } = state.screenSize
    if (width <= 0 || height <= 0) {
      return
    }
    void reportPlaybackHeartbeatWithProofOfPlay()
  },
  SCREEN_LAYOUT_HEARTBEAT_DEBOUNCE_MS,
  { leading: false, trailing: true }
)

/** Plug / unplug changes the dashboard power chip; do not wait up to a full heartbeat interval (#2528). */
const debouncedReportPowerSourceHeartbeat = debounce(
  () => {
    const tenant = state.tenant
    if (tenant == null || tenant.quotaExcess === true) {
      return
    }
    void reportPlaybackHeartbeatWithProofOfPlay()
  },
  POWER_SOURCE_HEARTBEAT_DEBOUNCE_MS,
  { leading: false, trailing: true }
)

screen.on(ScreenEvent.SIZE, () => {
  debouncedReportScreenLayoutHeartbeat()
})
screen.on(ScreenEvent.ROTATION, () => {
  debouncedReportScreenLayoutHeartbeat()
})

/**
 * {@link App.vue} mounts {@link VBuffer} only when there is a non-empty queue and the post-playlist hold is off.
 * Otherwise the idle layer is shown and VBuffer’s heartbeat cadence never runs — so viewport/storage metadata
 * (dashboard aspect ratio) must be sent from here.
 */
let idleRootPlaybackHeartbeatCadence: PlaybackHeartbeatCadenceHandle | null = null

function clearIdleRootPlaybackHeartbeatCadence(): void {
  idleRootPlaybackHeartbeatCadence?.clear()
  idleRootPlaybackHeartbeatCadence = null
}

function syncPlaybackHeartbeatWatchdog(): void {
  const tenant = state.tenant
  if (tenant == null || tenant.quotaExcess === true) {
    clearPlaybackHeartbeatWatchdog()
    return
  }
  armPlaybackHeartbeatWatchdog({
    shouldExpectHeartbeat: () => state.tenant != null && state.tenant.quotaExcess !== true
  })
}

function syncIdleRootPlaybackHeartbeatCadence(): void {
  const tenant = state.tenant
  const quotaBlocked = tenant?.quotaExcess === true
  const weeklyBlackout = tenant != null && !quotaBlocked && isWeeklyAvailabilitySuppressingPlaybackNow()
  const idleLike = tenant != null && !quotaBlocked && (state.queue.length < 1 || state.postPlaylistLoadHoldActive)
  if (!idleLike && !weeklyBlackout) {
    clearIdleRootPlaybackHeartbeatCadence()
    return
  }
  if (idleRootPlaybackHeartbeatCadence?.isArmed()) {
    return
  }
  pokeWeeklyAvailabilityGateAndResync()
  // Jittered phase + interval (#1257): avoid synchronized herds after mass reconnect / idle arm.
  idleRootPlaybackHeartbeatCadence = armPlaybackHeartbeatCadence({
    baseIntervalMs: PLAYBACK_HEARTBEAT_INTERVAL_MS,
    onTick: () => {
      pokeWeeklyAvailabilityGateAndResync()
      void reportPlaybackHeartbeatWithProofOfPlay()
    }
  })
}

watch(
  () =>
    [
      state.tenant?.id,
      state.tenant?.quotaExcess === true,
      state.queue.length,
      state.postPlaylistLoadHoldActive,
      state.weeklyAvailabilityGateEpoch,
      state.availabilityWeeklySchedule
    ] as const,
  () => {
    syncIdleRootPlaybackHeartbeatCadence()
    syncPlaybackHeartbeatWatchdog()
  },
  { immediate: true, deep: true }
)

watch(
  () =>
    [
      state.tenant?.id,
      state.tenant?.quotaExcess === true,
      state.queue.length,
      state.postPlaylistLoadHoldActive,
      state.weeklyAvailabilityGateEpoch,
      state.availabilityWeeklySchedule
    ] as const,
  () => {
    const tenant = state.tenant
    const quotaBlocked = tenant?.quotaExcess === true
    const weeklyBlackout = tenant != null && !quotaBlocked && isWeeklyAvailabilitySuppressingPlaybackNow()
    const playbackActive = tenant != null
      && !quotaBlocked
      && !weeklyBlackout
      && state.queue.length > 0
      && !state.postPlaylistLoadHoldActive
    syncPlayerLivePreviewUploadCadence({
      enabled: playbackActive,
      apiClientService
    })
  },
  { immediate: true, deep: true }
)

let wasNetworkConnected = typeof navigator !== 'undefined' && navigator.onLine
networkStatus.on(NetworkStatusEvent.UPDATE, (nextStatus) => {
  state.networkStatus = nextStatus
  if (nextStatus.connected) {
    void flushHeartbeatQueue()
    if (!wasNetworkConnected) {
      debouncedReloadPlaybackAssets()
    }
  }
  wasNetworkConnected = nextStatus.connected
})

geolocationService.on(GeolocationServiceEvent.RUNNING, (geolocationRunning) => {
  state.geolocationRunning = geolocationRunning
})

geolocationService.on(GeolocationServiceEvent.POSITION, (geolocationPosition) => {
  state.geolocationPosition = geolocationPosition
  const materializerRoot = playbackMaterializationService.getPlaybackScheduleRootEffective()
  if (playbackScheduleHasGeoFilter(state.campaign ?? materializerRoot)) {
    syncPlaybackGeoPointFromStateDebounced()
  }
})

geolocationService.on(GeolocationServiceEvent.POSITION_DMS, (geolocationPositionDMS) => {
  state.geolocationPositionDMS = geolocationPositionDMS
})

geolocationService.on(GeolocationServiceEvent.POSITION_UPDATES, (geolocationPositionUpdates) => {
  state.geolocationPositionUpdates = geolocationPositionUpdates
})

/** Debounce GPS-driven queue rebuilds when a schedule has geofilters (mobile/vehicle). */
const syncPlaybackGeoPointFromStateDebounced = debounce(() => {
  syncPlaybackGeoPointFromState()
}, 2_000)

/**
 * Keep materialization geofilter evaluation in sync with live GPS.
 * Prefer an explicit root (the schedule being applied) over {@link state.campaign}, which can lag
 * during soft-defer resync or be null while the materializer still holds a geo-filtered schedule —
 * clearing the geo point in that window emptied the queue (hibernation clock) even with a GPS fix.
 */
function syncPlaybackGeoPointFromState(rootOverride?: IPlaybackScheduleRoot | null): void {
  const materializerRoot = playbackMaterializationService.getPlaybackScheduleRootEffective()
  let scheduleForGeo: IPlaybackScheduleRoot | null = null
  if (rootOverride !== undefined) {
    scheduleForGeo = playbackScheduleHasGeoFilter(rootOverride) ? rootOverride : null
  }
  else {
    const root = state.campaign ?? materializerRoot
    scheduleForGeo = playbackScheduleHasGeoFilter(root)
      ? root
      : (playbackScheduleHasGeoFilter(materializerRoot) ? materializerRoot : null)
  }
  if (scheduleForGeo == null) {
    playbackMaterializationService.setGeoPointForPlayback(null)
    return
  }
  const point = resolvePlaybackGeoPointForSchedule(state.geolocationPosition, scheduleForGeo)
  playbackMaterializationService.setGeoPointForPlayback(point)
}

let postPlaylistLoadHoldTimer: ReturnType<typeof setTimeout> | null = null
/** Wall time when the current campaign load entered LOADING; used to skip download chrome/hold if load finishes quickly. */
let campaignLoadingStartedAt: number | null = null

playbackMaterializationService.on(PlaybackScheduleMaterializationEvent.STATE_UPDATE, (campaignState) => {
  state.loadingCampaign = campaignState === PlaybackScheduleMaterializationState.LOADING
  if (campaignState === PlaybackScheduleMaterializationState.LOADING) {
    campaignLoadingStartedAt = Date.now()
  }
  else if (state.queue.length < 1) {
    campaignLoadingStartedAt = null
  }
  if (campaignState === PlaybackScheduleMaterializationState.READY || campaignState === PlaybackScheduleMaterializationState.IDLE) {
    state.campaignLoadError = null
  }
})
playbackMaterializationService.on(PlaybackScheduleMaterializationEvent.LOAD_ERROR, ({ message }) => {
  state.campaignLoadError = message
  campaignLoadingStartedAt = null
})

function publishPlayerGlassSample(): void {
  setPlayerGlassDiagnosticsSample({
    glassState: resolvePlayerGlassState({
      paired: state.tenant != null,
      playableCount: state.queue.length,
      skippedUncachedCount: state.skippedUncachedFrameCount,
      hasPublishedSchedule: state.campaign != null
    }),
    skippedUncachedFrameCount: state.skippedUncachedFrameCount
  })
}

playbackMaterializationService.on(PlaybackScheduleMaterializationEvent.QUEUE_UPDATE, (queue) => {
  state.queue = queue
  state.skippedUncachedFrameCount = playbackMaterializationService.skippedUncachedFrameCount
  publishPlayerGlassSample()
})

playbackMaterializationService.on(PlaybackScheduleMaterializationEvent.SKIPPED_UNCACHED, (skipped) => {
  state.skippedUncachedFrameCount = skipped
  publishPlayerGlassSample()
})

/**
 * Timers overlay polling lives in {@link VBuffer}, which unmounts when the queue is empty. Without this reset,
 * campaign/frame lines keep the last values from the previous loop (e.g. master playlist unset → “Aguardando programação”).
 */
watch(
  () => [state.showTimersOverlay, state.queue.length] as const,
  ([show, len]) => {
    if (show && len < 1) {
      resetTimersOverlayForIdlePlayback()
      setWidgetRevealTiming(null)
    }
  },
  { immediate: true }
)

playbackMaterializationService.on(PlaybackScheduleMaterializationEvent.ASSETS_UPDATE, () => {
  state.campaignAssetsVersion = Date.now()
})

watch(
  () => [state.tenant != null, state.campaign != null, state.queue.length, state.skippedUncachedFrameCount] as const,
  () => {
    publishPlayerGlassSample()
  },
  { immediate: true }
)

watch(
  () => [state.tenant, state.queue.length] as const,
  ([tenant, len], prev) => {
    const prevLen = prev?.[1] ?? -1
    const prevTenant = prev?.[0] ?? null
    /**
     * `registration-status` often updates `tenant` while the post-playlist hold timer is running. Clearing the timer
     * without clearing {@link state.postPlaylistLoadHoldActive} left the idle/loading layer (`App.vue` branch) stuck
     * or re-toggled — visible flicker over `VBuffer`.
     */
    const startingFirstQueueHold = tenant !== null && len >= 1 && prevLen < 1 && len >= 1
    const registrationRefreshDuringPlayback
      = len >= 1
        && prevLen >= 1
        && tenant !== null
        && prevTenant !== null
        && canonicalStringify(tenant) !== canonicalStringify(prevTenant)
    if (registrationRefreshDuringPlayback) {
      if (postPlaylistLoadHoldTimer != null) {
        clearTimeout(postPlaylistLoadHoldTimer)
        postPlaylistLoadHoldTimer = null
      }
      state.postPlaylistLoadHoldActive = false
      return
    }
    if (postPlaylistLoadHoldTimer !== null) {
      clearTimeout(postPlaylistLoadHoldTimer)
      postPlaylistLoadHoldTimer = null
      if (!startingFirstQueueHold) {
        state.postPlaylistLoadHoldActive = false
      }
    }
    if (tenant === null || len < 1) {
      state.postPlaylistLoadHoldActive = false
      return
    }
    if (prevLen < 1 && len >= 1) {
      const startedAt = campaignLoadingStartedAt
      campaignLoadingStartedAt = null
      const fastLoad
        = startedAt != null && Date.now() - startedAt < MEDIA_LOAD_CHROME_DELAY_MS
      if (fastLoad) {
        state.postPlaylistLoadHoldActive = false
        return
      }
      const reducedMotion
        = typeof window !== 'undefined'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const holdMs = reducedMotion ? 0 : POST_PLAYLIST_LOAD_HOLD_MS
      if (holdMs <= 0) {
        state.postPlaylistLoadHoldActive = false
        return
      }
      state.postPlaylistLoadHoldActive = true
      postPlaylistLoadHoldTimer = setTimeout(() => {
        state.postPlaylistLoadHoldActive = false
        postPlaylistLoadHoldTimer = null
      }, holdMs)
    }
  }
)

/** Canonical tenant payload; skips reactive churn when registration-status refetches identical JSON. */
let lastTenantRegistrationCanonical = ''

registrationStatusService.on(RegistrationStatusServiceEvent.TENANT_UPDATE, (tenant) => {
  const canon = tenant === null ? '\0tenant:null' : canonicalStringify(tenant)
  if (canon !== lastTenantRegistrationCanonical) {
    lastTenantRegistrationCanonical = canon
    state.tenant = tenant
    if (tenant != null) {
      state.registrationStatusReceivedAt = 0
      // Pairing flags are cleared on tenant REGISTRATION only — not here.
      // Stale in-flight status must not wipe QR after unregister (#1438).
    }
    if (tenant === null) {
      state.audienceAnalyticsEnabled = false
      state.audienceVideoInputDeviceId = null
      state.registrationScheduleRoots = []
    }
    syncPlaybackAspectRatioFilter()
  }
  if (tenant != null) {
    void syncAndroidKioskLockPolicy(tenant.androidKioskLockEnabled !== false)
    // Live PIN rotate/clear while the keypad is open (do not keep validating a stale snapshot).
    refreshKioskPromptExpectedPin(tenant.fieldServicePin)
  }
  if (tenant?.quotaExcess === true) {
    clearDeferredRegistrationPlaybackSchedule()
    void playbackMaterializationService.setPlaybackSchedule(null)
  }
})

registrationStatusService.on(RegistrationStatusServiceEvent.SCHEDULE_ROOTS_UPDATE, (roots) => {
  state.registrationScheduleRoots = roots ?? []
})

registrationStatusService.on(RegistrationStatusServiceEvent.PLAYER_GROUP_UPDATE, (playerGroup) => {
  state.playerGroup = playerGroup
})

registrationStatusService.on(RegistrationStatusServiceEvent.PLAYER_INFO_UPDATE, (info) => {
  state.playerTitle = info?.title ?? ''
  state.playerDescription = info?.description ?? null
  state.audienceAnalyticsEnabled = info?.audienceAnalyticsEnabled === true
  state.audienceVideoInputDeviceId = info?.audienceVideoInputDeviceId ?? null
})

const CACHED_ASSET_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000 // 1 week: only evict assets not used in the last week (minimizes re-downloads)

/**
 * Age-based cache eviction that never removes assets referenced by the given schedule roots
 * (incoming campaign and/or the currently loaded root). Without this, a player offline for
 * longer than {@link CACHED_ASSET_MAX_AGE_MS} would purge the very media its cached
 * registration snapshot still needs for offline playback.
 */
async function purgeAgedCachedAssetsProtectingSchedules(...roots: (IPlaybackScheduleRoot | null)[]): Promise<void> {
  const protectedUrns = new Set<string>()
  for (const root of roots) {
    for (const urn of collectScheduleRootAssetUrns(root)) {
      protectedUrns.add(urn)
    }
  }
  await dbService.purgeCachedAssetsOlderThan(CACHED_ASSET_MAX_AGE_MS, protectedUrns)
}

/** New schedule from registration-status; applied after head ready + frame boundary (VBuffer notifies). */
let deferredRegistrationPlaybackRoot: IPlaybackScheduleRoot | null = null
/**
 * Forces apply if VBuffer never reports a frame/loop boundary (Android same-id video loop, stuck
 * pending surface commit) — field lag was 80–200s+ without a ceiling (#1577). While downloading,
 * delay is on-air remaining when known, else ~45s, never more than 120s. Once head is
 * `ready_at_boundary`, {@link rearmDeferredFlushAfterReadyAtBoundary} shortens the remaining wait
 * (2.5s cap) so long dwells do not stall assign. Also forces best-effort rematch if head prefetch
 * never reaches ready_at_boundary.
 */
let deferredRegistrationPlaybackFlushTimer: ReturnType<typeof setTimeout> | null = null
let deferredRegistrationPlaybackFlushArmGen = 0
/** Absolute wall deadline for starvation force-flush (set when arming defer). */
let deferredRegistrationPlaybackDeadlineAtMs = 0
/** Cancels in-flight background prefetch when defer clears or a newer pending root arrives. */
let pendingPlaylistPrefetchGen = 0
/** Head+preload URNs for the current pending root (ready gate). */
let pendingPlaylistHeadUrns: string[] = []
/** Fingerprint of last applied head URN set (ready-phase race vs schedule-window change). */
let pendingPlaylistHeadKey: string | null = null
/**
 * Bumped on every progress refresh start; stale IDB scans must not overwrite a newer result
 * (DOWNLOAD_PROGRESS races ready → downloading).
 */
let pendingPlaylistProgressRefreshSeq = 0
/** Coalesce bursty DOWNLOAD_PROGRESS into one microtask refresh. */
let pendingPlaylistProgressRefreshQueued = false
/** VBuffer registers this; invoked once after registration schedule materialization during playback. */
let registrationPlaybackResyncHandler: (() => Promise<void>) | null = null
export function registerRegistrationPlaybackResyncHandler(handler: (() => Promise<void>) | null): void {
  registrationPlaybackResyncHandler = handler
}

/**
 * VBuffer registers this; after asset-cache repair + schedule reload, force remount binary media
 * so stuck `<video>` pipelines recover even when IndexedDB blobs were not purged (same Blob → no
 * automatic assetIndex remount).
 */
let cacheRepairMediaRemountHandler: (() => Promise<void>) | null = null
export function registerCacheRepairMediaRemountHandler(handler: (() => Promise<void>) | null): void {
  cacheRepairMediaRemountHandler = handler
}

registerPlaylistSwapDiagnosticsProvider(() => {
  const feedId = getPlaybackScheduleMaterializationIdentity(state.campaign)
  const loadedId = getPlaybackScheduleMaterializationIdentity(
    playbackMaterializationService.getLastSetPlaybackRoot()
  )
  const pendingRoot = deferredRegistrationPlaybackRoot
  const nextId = getPlaybackScheduleMaterializationIdentity(pendingRoot)
    ?? feedId
  const playingSomething
    = state.queue.length > 0
      && playbackMaterializationService.state === PlaybackScheduleMaterializationState.READY
  const onAirSeq = getQueueSequenceHash(state.queue)
  const peekSeq = playbackMaterializationService.peekPlaybackSequenceHashNow()
  const swap = state.pendingPlaylistSwap
  const feedAhead = isFeedAheadOfMaterialization({
    playingSomething,
    uiId: feedId,
    nextId,
    loadedId,
    onAirSeq,
    peekSeq
  })
  if (swap != null) {
    const timing = pendingPlaylistSwapTiming
    return {
      feedPlaylistIdentity: feedId,
      materializedPlaylistIdentity: loadedId,
      pendingPlaylistSwapPhase: swap.phase,
      pendingPlaylistSwapProgress01: swap.progress01,
      pendingPlaylistSwapTitle: swap.title,
      pendingPlaylistSwapFlushReason: timing?.flushReason ?? null,
      pendingPlaylistSwapArmedAtMs: timing?.armedAtMs ?? null,
      pendingPlaylistSwapReadyAtMs: timing?.readyAtMs ?? null,
      pendingPlaylistSwapFlushStartedAtMs: timing?.flushStartedAtMs ?? null,
      pendingPlaylistSwapFlushEndedAtMs: null,
      feedAheadOfMaterialization: feedAhead
    }
  }
  const sticky = lastCompletedPlaylistSwapDiagnostics
  if (sticky != null && Date.now() < lastCompletedPlaylistSwapUntilMs) {
    return {
      ...sticky,
      feedPlaylistIdentity: feedId,
      materializedPlaylistIdentity: loadedId,
      feedAheadOfMaterialization: feedAhead
    }
  }
  return {
    feedPlaylistIdentity: feedId,
    materializedPlaylistIdentity: loadedId,
    pendingPlaylistSwapPhase: null,
    pendingPlaylistSwapProgress01: null,
    pendingPlaylistSwapTitle: null,
    pendingPlaylistSwapFlushReason: null,
    pendingPlaylistSwapArmedAtMs: null,
    pendingPlaylistSwapReadyAtMs: null,
    pendingPlaylistSwapFlushStartedAtMs: null,
    pendingPlaylistSwapFlushEndedAtMs: null,
    feedAheadOfMaterialization: feedAhead
  }
})

/** Soft-defer arm → rematch timings (live); cleared with swap state. */
let pendingPlaylistSwapTiming: {
  armedAtMs: number
  readyAtMs: number | null
  flushStartedAtMs: number | null
  flushReason: TPendingPlaylistSwapFlushReason | null
} | null = null

/**
 * Last successful soft-defer cutover — sticky on heartbeats ~2min so get_player can read
 * wait vs rematch deltas after paint (cadence is ~1/min).
 */
let lastCompletedPlaylistSwapDiagnostics: {
  pendingPlaylistSwapPhase: null
  pendingPlaylistSwapProgress01: number | null
  pendingPlaylistSwapTitle: string | null
  pendingPlaylistSwapFlushReason: TPendingPlaylistSwapFlushReason | null
  pendingPlaylistSwapArmedAtMs: number | null
  pendingPlaylistSwapReadyAtMs: number | null
  pendingPlaylistSwapFlushStartedAtMs: number | null
  pendingPlaylistSwapFlushEndedAtMs: number | null
} | null = null
let lastCompletedPlaylistSwapUntilMs = 0
const LAST_COMPLETED_PLAYLIST_SWAP_DIAG_TTL_MS = 120_000

function pokePlaylistSwapDiagnosticsHeartbeat(): void {
  void reportPlaybackHeartbeatWithProofOfPlay()
}

function clearPendingPlaylistSwapState(options?: { recordCompleted?: boolean }): void {
  if (options?.recordCompleted === true && pendingPlaylistSwapTiming != null) {
    const endedAtMs = Date.now()
    lastCompletedPlaylistSwapDiagnostics = {
      pendingPlaylistSwapPhase: null,
      pendingPlaylistSwapProgress01: state.pendingPlaylistSwap?.progress01 ?? 1,
      pendingPlaylistSwapTitle: state.pendingPlaylistSwap?.title ?? null,
      pendingPlaylistSwapFlushReason: pendingPlaylistSwapTiming.flushReason,
      pendingPlaylistSwapArmedAtMs: pendingPlaylistSwapTiming.armedAtMs,
      pendingPlaylistSwapReadyAtMs: pendingPlaylistSwapTiming.readyAtMs,
      pendingPlaylistSwapFlushStartedAtMs: pendingPlaylistSwapTiming.flushStartedAtMs,
      pendingPlaylistSwapFlushEndedAtMs: endedAtMs
    }
    lastCompletedPlaylistSwapUntilMs = endedAtMs + LAST_COMPLETED_PLAYLIST_SWAP_DIAG_TTL_MS
  }
  pendingPlaylistPrefetchGen++
  pendingPlaylistProgressRefreshSeq++
  pendingPlaylistProgressRefreshQueued = false
  pendingPlaylistHeadUrns = []
  pendingPlaylistHeadKey = null
  pendingPlaylistSwapFlushEtaAtMs = null
  pendingPlaylistSwapTiming = null
  state.pendingPlaylistSwap = null
}

/** Last flush ETA computed before/while swap state exists (arm may run before prefetch sets swap). */
let pendingPlaylistSwapFlushEtaAtMs: number | null = null

function setPendingPlaylistSwapFromRoot(
  root: IPlaybackScheduleRoot,
  progress01: number,
  phase: TPendingPlaylistSwapPhase
): void {
  clearRegistrationStatusRefreshPendingNow()
  const { title, version } = resolvePendingPlaylistSwapTitle(root)
  const prevEta = state.pendingPlaylistSwap?.etaAtMs ?? null
  const previousPhase = state.pendingPlaylistSwap?.phase ?? null
  if (pendingPlaylistSwapTiming == null) {
    pendingPlaylistSwapTiming = {
      armedAtMs: Date.now(),
      readyAtMs: null,
      flushStartedAtMs: null,
      flushReason: null
    }
  }
  if (
    previousPhase !== 'ready_at_boundary'
    && previousPhase !== 'rematching'
    && phase === 'ready_at_boundary'
    && pendingPlaylistSwapTiming.readyAtMs == null
  ) {
    pendingPlaylistSwapTiming.readyAtMs = Date.now()
  }
  state.pendingPlaylistSwap = {
    title,
    ...(version != null ? { version } : {}),
    progress01,
    phase,
    etaAtMs: prevEta ?? pendingPlaylistSwapFlushEtaAtMs
  }
}

function setPendingPlaylistSwapEtaAtMs(etaAtMs: number | null): void {
  pendingPlaylistSwapFlushEtaAtMs = etaAtMs
  if (state.pendingPlaylistSwap == null) {
    return
  }
  state.pendingPlaylistSwap = {
    ...state.pendingPlaylistSwap,
    etaAtMs
  }
}

function schedulePendingPlaylistSwapProgressRefresh(): void {
  const pending = deferredRegistrationPlaybackRoot
  if (pending == null || state.pendingPlaylistSwap == null) {
    return
  }
  if (pendingPlaylistProgressRefreshQueued) {
    return
  }
  pendingPlaylistProgressRefreshQueued = true
  queueMicrotask(() => {
    pendingPlaylistProgressRefreshQueued = false
    const root = deferredRegistrationPlaybackRoot
    if (root == null || state.pendingPlaylistSwap == null) {
      return
    }
    void refreshPendingPlaylistSwapProgress(pendingPlaylistPrefetchGen, root)
  })
}

async function countCachedPendingHeadUrns(
  headUrns: readonly string[],
  root: IPlaybackScheduleRoot
): Promise<Set<string>> {
  const cached = new Set<string>()
  for (const urn of headUrns) {
    if (pendingPlaylistUrnReadyWithoutBinary(urn, root)) {
      cached.add(urn)
      continue
    }
    let row: Awaited<ReturnType<typeof dbService.getCachedAsset>> = null
    try {
      row = await dbService.getCachedAsset(urn)
    }
    catch {
      // IDB blip — still try hybrid disk below.
      row = null
    }
    if (row != null) {
      cached.add(urn)
      continue
    }
    try {
      // Android hybrid: native file can outlive the IDB row. Soft-defer ready must use
      // cheap existence (stat), not magic sniff — sniff false-negatives skip ready and
      // fall through to the 45s unknown-remaining force (#1671 soak ~45s reverse).
      // MIME sniff stays in AssetsService recover. P2 keys are bare URNs.
      if ((await hybridPlaybackFileStat(urn)) != null) {
        cached.add(urn)
      }
    }
    catch {
      // Treat as miss; fetchAssets will retry.
    }
  }
  return cached
}

async function refreshPendingPlaylistSwapProgress(gen: number, root: IPlaybackScheduleRoot): Promise<void> {
  const seq = ++pendingPlaylistProgressRefreshSeq
  if (gen !== pendingPlaylistPrefetchGen || deferredRegistrationPlaybackRoot !== root) {
    return
  }
  // Re-resolve head at “now” with live filters so cutover matches materialization.
  const previousHeadKey = pendingPlaylistHeadKey
  pendingPlaylistHeadUrns = collectPendingPlaylistHeadAssetUrns(
    root,
    getLocalAsUtcInstant(),
    playbackMaterializationService.getResolveSegmentOptions()
  )
  const headKey = pendingPlaylistHeadUrns.join('\0')
  const cached = await countCachedPendingHeadUrns(pendingPlaylistHeadUrns, root)
  if (
    seq !== pendingPlaylistProgressRefreshSeq
    || gen !== pendingPlaylistPrefetchGen
    || deferredRegistrationPlaybackRoot !== root
  ) {
    return
  }
  const progress01 = pendingPlaylistHeadReadyProgress01(pendingPlaylistHeadUrns, root, cached)
  const previousPhase = state.pendingPlaylistSwap?.phase ?? null
  const phase = resolvePendingPlaylistSwapPhaseUpdate({
    previousPhase,
    progress01,
    headKey,
    previousHeadKey
  })
  pendingPlaylistHeadKey = headKey
  const displayProgress = phase === 'ready_at_boundary' ? Math.max(progress01, 1) : progress01
  setPendingPlaylistSwapFromRoot(root, displayProgress, phase)
  if (previousPhase !== 'ready_at_boundary' && phase === 'ready_at_boundary') {
    rearmDeferredFlushAfterReadyAtBoundary()
    pokePlaylistSwapDiagnosticsHeartbeat()
  }
}

/**
 * Prefetch pending schedule assets without calling {@link setPlaybackSchedule}.
 * Ready gate is the **first on-air frame** only — fetch that first so cutover is not blocked on
 * preload or the rest of the campaign. Preload starts in parallel after head; campaign-tail rest
 * waits for rematch (loader background) so it does not steal download concurrency. When head is
 * already cached, do **not** await fetchAssets (verification / network must not delay
 * `ready_at_boundary` cutover).
 */
function startPendingPlaylistPrefetch(root: IPlaybackScheduleRoot): void {
  const gen = ++pendingPlaylistPrefetchGen
  pendingPlaylistHeadKey = null
  const resolveOpts = playbackMaterializationService.getResolveSegmentOptions()
  const now = getLocalAsUtcInstant()
  pendingPlaylistHeadUrns = collectPendingPlaylistHeadAssetUrns(root, now, resolveOpts)
  const preloadUrns = collectPendingPlaylistPreloadAssetUrns(root, now, resolveOpts)
  pendingPlaylistSwapTiming = {
    armedAtMs: Date.now(),
    readyAtMs: null,
    flushStartedAtMs: null,
    flushReason: null
  }
  setPendingPlaylistSwapFromRoot(root, 0, 'downloading')
  pokePlaylistSwapDiagnosticsHeartbeat()
  void (async () => {
    await refreshPendingPlaylistSwapProgress(gen, root)
    if (gen !== pendingPlaylistPrefetchGen || deferredRegistrationPlaybackRoot !== root) {
      return
    }
    const meta = isScheduleRootAssignment(root) ? root : null
    const headUrns = [...pendingPlaylistHeadUrns]
    const alreadyReady = state.pendingPlaylistSwap?.phase === 'ready_at_boundary'

    const fetchHead = (): Promise<unknown> => {
      if (headUrns.length === 0) {
        return Promise.resolve()
      }
      return assetsService.fetchAssets(
        headUrns,
        meta?.widgetAssetMetadata,
        meta?.hlsVideoAssetMetadata
      )
    }

    if (alreadyReady) {
      // IDB/metadata already playable — cutover timer is armed; warm/verify in background only.
      void fetchHead()
        .then(async () => {
          if (gen !== pendingPlaylistPrefetchGen || deferredRegistrationPlaybackRoot !== root) {
            return
          }
          await refreshPendingPlaylistSwapProgress(gen, root)
        })
        .catch((err) => {
          console.warn('[usePlayer] pending playlist head warm-fetch failed', err)
        })
      if (preloadUrns.length > 0) {
        void assetsService.fetchAssets(
          [...preloadUrns],
          meta?.widgetAssetMetadata,
          meta?.hlsVideoAssetMetadata
        ).catch((err) => {
          console.warn('[usePlayer] pending playlist preload prefetch failed', err)
        })
      }
      // Do not fetchRest here — rematch `localOnly` + loader background rest owns the tail.
      // Starting rest early steals ASSET_BINARY_FETCH_CONCURRENCY from rematch head await.
      return
    }

    try {
      // Head first when not ready — preload must not steal ASSET_BINARY_FETCH_CONCURRENCY
      // slots while soft-defer is still waiting to arm ready_at_boundary.
      await fetchHead()
      if (gen !== pendingPlaylistPrefetchGen || deferredRegistrationPlaybackRoot !== root) {
        return
      }
      await refreshPendingPlaylistSwapProgress(gen, root)
      if (gen !== pendingPlaylistPrefetchGen || deferredRegistrationPlaybackRoot !== root) {
        return
      }
      if (preloadUrns.length > 0) {
        void assetsService.fetchAssets(
          [...preloadUrns],
          meta?.widgetAssetMetadata,
          meta?.hlsVideoAssetMetadata
        ).catch((err) => {
          console.warn('[usePlayer] pending playlist preload prefetch failed', err)
        })
      }
      // Tail warms after cutover (see alreadyReady branch). Prefetch rest during the wait made
      // Campanha→Publicar rematch compete with ~20 large video GETs on Slimbox.
    }
    catch (err) {
      console.warn('[usePlayer] pending playlist prefetch failed', err)
      if (gen === pendingPlaylistPrefetchGen && deferredRegistrationPlaybackRoot === root) {
        // Late fetchHead failure must not clear rematching — that re-opens flush eligibility and
        // can rearm a forced cutover over an apply already in flight (Bugbot #1677).
        if (state.pendingPlaylistSwap?.phase === 'rematching') {
          return
        }
        // Best-effort: allow boundary/ceiling flush even if some assets failed.
        const wasReady = state.pendingPlaylistSwap?.phase === 'ready_at_boundary'
        setPendingPlaylistSwapFromRoot(root, state.pendingPlaylistSwap?.progress01 ?? 0, 'ready_at_boundary')
        if (!wasReady) {
          rearmDeferredFlushAfterReadyAtBoundary()
        }
      }
    }
  })()
}

/**
 * Once head is ready (`ready_at_boundary`), re-schedule the force timer with the snappy ready remaining
 * cap (does not reset the absolute 120s starvation deadline). Frame/hybrid boundaries still
 * flush earlier when they fire first.
 *
 * Arm the ready-cap timer **immediately** (unknown remaining → 2.5s). Refining with live
 * on-air timing is best-effort — never block cutover on a hung `getPlaybackTiming`.
 */
function rearmDeferredFlushAfterReadyAtBoundary(): void {
  if (deferredRegistrationPlaybackRoot == null) {
    return
  }
  if (state.pendingPlaylistSwap?.phase !== 'ready_at_boundary') {
    return
  }
  // Invalidate in-flight `armDeferredRegistrationPlaybackFlushTimer` timing callbacks so they
  // cannot overwrite the ready remaining cap with the full on-air remaining.
  deferredRegistrationPlaybackFlushArmGen++
  const gen = deferredRegistrationPlaybackFlushArmGen
  const deadlineAtMs = deferredRegistrationPlaybackDeadlineAtMs > 0
    ? deferredRegistrationPlaybackDeadlineAtMs
    : Date.now() + DEFERRED_REGISTRATION_PLAYBACK_MAX_MS
  const onAirExpectsVideo = onAirQueueFrameExpectsVideo(
    state.queue,
    state.playbackTiming?.notifiedIdx
  )
  scheduleDeferredRegistrationPlaybackFlush(
    deferredRegistrationForceDelayUntilDeadlineMs({
      deadlineAtMs,
      frameRemainingMs: null,
      readyAtBoundary: true,
      onAirExpectsVideo
    })
  )
  void (async () => {
    let remaining: number | null = null
    try {
      remaining = onAirFrameRemainingMs(
        await getSharedWorkersService().playbackScheduler.getPlaybackTiming()
      )
    }
    catch {
      remaining = null
    }
    if (gen !== deferredRegistrationPlaybackFlushArmGen || deferredRegistrationPlaybackRoot == null) {
      return
    }
    if (state.pendingPlaylistSwap?.phase !== 'ready_at_boundary') {
      return
    }
    if (remaining == null || !Number.isFinite(remaining)) {
      return
    }
    // Only reschedule when remaining is shorter than the ready cap already armed.
    if (remaining >= DEFERRED_REGISTRATION_PLAYBACK_READY_MAX_REMAINING_MS) {
      return
    }
    const delay = deferredRegistrationForceDelayUntilDeadlineMs({
      deadlineAtMs,
      frameRemainingMs: remaining,
      readyAtBoundary: true,
      onAirExpectsVideo
    })
    if (delay <= 0) {
      void flushDeferredRegistrationPlaybackScheduleAfterFrameBoundary()
      return
    }
    scheduleDeferredRegistrationPlaybackFlush(delay)
  })()
}

function disarmDeferredRegistrationPlaybackFlushTimeout(): void {
  if (deferredRegistrationPlaybackFlushTimer != null) {
    clearTimeout(deferredRegistrationPlaybackFlushTimer)
    deferredRegistrationPlaybackFlushTimer = null
  }
}

function clearDeferredRegistrationPlaybackFlushTimer(): void {
  deferredRegistrationPlaybackFlushArmGen++
  disarmDeferredRegistrationPlaybackFlushTimeout()
}

function scheduleDeferredRegistrationPlaybackFlush(delayMs: number): void {
  disarmDeferredRegistrationPlaybackFlushTimeout()
  const safeDelay = Math.max(0, delayMs)
  setPendingPlaylistSwapEtaAtMs(Date.now() + safeDelay)
  deferredRegistrationPlaybackFlushTimer = setTimeout(() => {
    deferredRegistrationPlaybackFlushTimer = null
    void flushDeferredRegistrationPlaybackScheduleAfterFrameBoundary({ force: true })
  }, safeDelay)
}

function armDeferredRegistrationPlaybackFlushTimer(options?: { preserveDeadline?: boolean }): void {
  const now = Date.now()
  const preserveRequested = options?.preserveDeadline === true
  const existingDeadline = deferredRegistrationPlaybackDeadlineAtMs
  const deadlineExpired = existingDeadline > 0 && existingDeadline <= now
  /**
   * Same pending root after the 120s ceiling: force apply now. Do not open a fresh window
   * (registration polls would otherwise postpone forever once the first deadline elapses).
   */
  if (preserveRequested && deadlineExpired) {
    clearDeferredRegistrationPlaybackFlushTimer()
    scheduleDeferredRegistrationPlaybackFlush(0)
    return
  }
  const preserve = preserveRequested && existingDeadline > now
  clearDeferredRegistrationPlaybackFlushTimer()
  if (!preserve) {
    deferredRegistrationPlaybackDeadlineAtMs = now + DEFERRED_REGISTRATION_PLAYBACK_MAX_MS
  }
  // Keep the pre-ready unknown/deadline force timer. Do **not** shorten it with on-air
  // remaining: scheduleDeferredRegistrationPlaybackFlush always forces, so a 3s still
  // remaining used to force rematch before head was ready and push the download wait into
  // apply (Campanha→Publicar ~40–55s). Ready rearm + frame boundaries own early cutover.
  scheduleDeferredRegistrationPlaybackFlush(
    deferredRegistrationForceDelayUntilDeadlineMs({
      deadlineAtMs: deferredRegistrationPlaybackDeadlineAtMs,
      frameRemainingMs: null
    })
  )
}

function clearDeferredRegistrationPlaybackSchedule(): void {
  clearDeferredRegistrationPlaybackFlushTimer()
  deferredRegistrationPlaybackRoot = null
  deferredRegistrationPlaybackDeadlineAtMs = 0
  pendingPlaylistSwapFlushEtaAtMs = null
  clearPendingPlaylistSwapState()
}

function serverAlignedNowInstant(): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(Date.now() + (state.serverClockOffsetMs || 0))
}

/** Device wall clock for weekly windows — same spine as playback scheduling (`getLocalAsUtcInstant`). */
function deviceWallClockNowInstant(): Temporal.Instant {
  return getLocalAsUtcInstant()
}

/** True when weekly schedule has blocks and device-local “now” is outside all windows. */
function isWeeklyAvailabilitySuppressingPlaybackNow(): boolean {
  const s = state.availabilityWeeklySchedule
  if (s == null || s.blocks.length === 0) {
    return false
  }
  return !isLocalWallClockInstantWithinPlayerWeeklyAvailability(s, deviceWallClockNowInstant())
}

/** Re-evaluate blackout UI and resume/clear materialized playback after wake, visibility, or window boundaries. */
function pokeWeeklyAvailabilityGateAndResync(): void {
  state.weeklyAvailabilityGateEpoch = Date.now()
  void resyncWeeklyAvailabilityPlayback()
}

async function resyncWeeklyAvailabilityPlayback(): Promise<void> {
  if (state.tenant == null || state.tenant.quotaExcess === true) {
    return
  }
  const suppressedBefore = isWeeklyAvailabilitySuppressingPlaybackNow()
  if (suppressedBefore) {
    clearDeferredRegistrationPlaybackSchedule()
    await playbackMaterializationService.setPlaybackSchedule(null)
  }
  else {
    if (deferredRegistrationPlaybackRoot !== null) {
      await flushDeferredRegistrationPlaybackScheduleAfterFrameBoundary()
      if (isWeeklyAvailabilitySuppressingPlaybackNow()) {
        return
      }
    }
    const root = state.campaign
    if (root == null) {
      return
    }
    const loaded = playbackMaterializationService.getLastSetPlaybackRoot()
    const queueEmpty = state.queue.length < 1
    if (loaded == null || campaignHash(loaded) !== campaignHash(root) || queueEmpty) {
      syncPlaybackAspectRatioFilter()
      await playbackMaterializationService.setPlaybackSchedule(root)
    }
  }
  if (suppressedBefore !== isWeeklyAvailabilitySuppressingPlaybackNow()) {
    void reportPlaybackHeartbeatWithProofOfPlay()
  }
}

let weeklyAvailabilityTicker: ReturnType<typeof setInterval> | null = null
let weeklyAvailabilityBoundaryTimer: ReturnType<typeof setTimeout> | null = null

function clearWeeklyAvailabilityBoundaryTimer(): void {
  if (weeklyAvailabilityBoundaryTimer != null) {
    clearTimeout(weeklyAvailabilityBoundaryTimer)
    weeklyAvailabilityBoundaryTimer = null
  }
}

function scheduleWeeklyAvailabilityBoundaryTimer(): void {
  clearWeeklyAvailabilityBoundaryTimer()
  const schedule = state.availabilityWeeklySchedule
  if (schedule == null || schedule.blocks.length === 0) {
    return
  }
  const msUntil = msUntilNextPlayerWeeklyAvailabilityTransitionLocalWallClock(
    schedule,
    deviceWallClockNowInstant()
  )
  if (msUntil == null || msUntil < 1) {
    return
  }
  const delayMs = delayMsForNextWeeklyAvailabilityBoundaryPoll(msUntil)
  weeklyAvailabilityBoundaryTimer = setTimeout(() => {
    weeklyAvailabilityBoundaryTimer = null
    pokeWeeklyAvailabilityGateAndResync()
    scheduleWeeklyAvailabilityBoundaryTimer()
  }, delayMs)
}

function syncWeeklyAvailabilityTicker(): void {
  const need
    = state.tenant != null
      && state.tenant.quotaExcess !== true
      && state.availabilityWeeklySchedule != null
      && state.availabilityWeeklySchedule.blocks.length > 0

  if (!need) {
    if (weeklyAvailabilityTicker != null) {
      clearInterval(weeklyAvailabilityTicker)
      weeklyAvailabilityTicker = null
    }
    clearWeeklyAvailabilityBoundaryTimer()
    return
  }
  scheduleWeeklyAvailabilityBoundaryTimer()
  if (weeklyAvailabilityTicker != null) {
    return
  }
  pokeWeeklyAvailabilityGateAndResync()
  weeklyAvailabilityTicker = setInterval(() => {
    pokeWeeklyAvailabilityGateAndResync()
  }, 1000)
}

watch(
  () =>
    [state.tenant?.id, state.tenant?.quotaExcess === true, state.availabilityWeeklySchedule] as const,
  () => {
    syncWeeklyAvailabilityTicker()
    void resyncWeeklyAvailabilityPlayback()
  },
  { deep: true, immediate: true }
)

function playbackScheduleRootHasMasterPlaylist(root: IPlaybackScheduleRoot): boolean {
  const masterLower = (state.assignedMasterPlaylistUrn?.trim() ?? '').toLowerCase()
  if (masterLower !== '') {
    return (root.playlists ?? []).some(p => p.playlistUrn.trim().toLowerCase() === masterLower)
  }
  return (root.playlists ?? []).some(p => playlistUrnIsPlayerMasterPlaylist(p.playlistUrn))
}

function syncOperatorMasterPlaylistUrnFromRegistration(payload: IPlayerRegistrationPayload | null): void {
  const urn = payload === null
    ? null
    : operatorAssignedPlayerMasterPlaylistUrn(
        payload.assignedMasterPlaylistUrn ?? payload.playerGroup?.masterPlaylistUrn ?? null
      )
  state.assignedMasterPlaylistUrn = urn
  playbackMaterializationService.setOperatorMasterPlaylistUrn(urn)
}

function registrationPlaybackDuplicateOfLoaded(campaign: IPlaybackScheduleRoot): boolean {
  const loaded = playbackMaterializationService.getLastSetPlaybackRoot()
  if (
    campaignHash(campaign) !== campaignHash(loaded)
    || getPlaybackScheduleMaterializationIdentity(campaign)
      !== getPlaybackScheduleMaterializationIdentity(loaded)
  ) {
    return false
  }
  /**
   * lastSet / Feed can already show the new playlist while the on-air queue is still the
   * previous one (soft-skip / failed rematch). Treat as not duplicate so we rematerialize.
   * Compare sequence hashes (not raw peek frames) so rotating embeds do not false-positive.
   */
  const onAirSeq = getQueueSequenceHash(state.queue)
  const peekSeq = playbackMaterializationService.peekPlaybackSequenceHashNow()
  return onAirSeq === peekSeq
}

async function applyCampaignRegistrationPayloadToPlayback(
  campaign: IPlaybackScheduleRoot | null,
  opts?: { awaitAssetUrns?: readonly string[] }
): Promise<void> {
  if (state.tenant !== null) {
    state.campaignUpdateReceivedAt = Date.now()
  }
  const playingReady
    = state.queue.length > 0
      && playbackMaterializationService.state === PlaybackScheduleMaterializationState.READY
  const nextId = campaign != null ? getPlaybackScheduleMaterializationIdentity(campaign) : null
  const loadedId = getPlaybackScheduleMaterializationIdentity(playbackMaterializationService.getLastSetPlaybackRoot())
  const scheduleIdentitySwapDuringPlayback
    = campaign != null
      && playingReady
      && nextId != null
      && (loadedId == null || nextId !== loadedId)
  state.registrationPlaybackResyncPending = scheduleIdentitySwapDuringPlayback
  if (campaign === null) {
    state.registrationPlaybackResyncPending = false
  }
  /**
   * Keep Feed/Conteúdo on the on-air root until rematch paints. Updating `state.campaign` at
   * flush start flipped the overlay label while VBuffer still held outgoing pixels (#1632/#1634).
   */
  const deferCampaignUiUntilResync = scheduleIdentitySwapDuringPlayback
  if (
    !deferCampaignUiUntilResync
    && !(campaign !== null && state.campaign !== null && campaignHash(state.campaign) === campaignHash(campaign))
  ) {
    state.campaign = campaign
  }
  if (campaign === null) {
    state.campaignLoadError = null
    await dbService.purgeCachedAssets()
    await clearWidgetSwCaches()
  }
  else {
    await purgeAgedCachedAssetsProtectingSchedules(campaign, playbackMaterializationService.getLastSetPlaybackRoot())
  }
  if (state.tenant?.quotaExcess === true || isWeeklyAvailabilitySuppressingPlaybackNow()) {
    await playbackMaterializationService.setPlaybackSchedule(null)
  }
  else {
    syncPlaybackAspectRatioFilter()
    syncPlaybackGeoPointFromState(campaign)
    await playbackMaterializationService.setPlaybackSchedule(
      campaign,
      opts?.awaitAssetUrns != null && opts.awaitAssetUrns.length > 0
        ? { awaitAssetUrns: opts.awaitAssetUrns }
        : undefined
    )
    syncPlaybackGeoPointFromState(campaign)
  }
  if (state.registrationPlaybackResyncPending && registrationPlaybackResyncHandler != null) {
    try {
      await registrationPlaybackResyncHandler()
    }
    finally {
      state.registrationPlaybackResyncPending = false
      if (
        deferCampaignUiUntilResync
        && campaign != null
        && !(state.campaign !== null && campaignHash(state.campaign) === campaignHash(campaign))
      ) {
        state.campaign = campaign
      }
    }
  }
  else if (
    deferCampaignUiUntilResync
    && campaign != null
    && !(state.campaign !== null && campaignHash(state.campaign) === campaignHash(campaign))
  ) {
    state.registrationPlaybackResyncPending = false
    state.campaign = campaign
  }
}

async function flushDeferredRegistrationPlaybackScheduleAfterFrameBoundary(
  options?: { force?: boolean }
): Promise<void> {
  for (;;) {
    const pending = deferredRegistrationPlaybackRoot
    if (pending == null) {
      return
    }
    const force
      = options?.force === true
        || (deferredRegistrationPlaybackDeadlineAtMs > 0
          && Date.now() >= deferredRegistrationPlaybackDeadlineAtMs)
    const phase = state.pendingPlaylistSwap?.phase ?? null
    if (!shouldApplyDeferredRegistrationPlaybackFlush({ phase, force })) {
      // Head not ready yet — keep deferring; starvation timer still armed.
      return
    }
    clearDeferredRegistrationPlaybackFlushTimer()
    const flushReason = resolvePendingPlaylistSwapFlushReason({ force, phase })
    if (pendingPlaylistSwapTiming == null) {
      pendingPlaylistSwapTiming = {
        armedAtMs: Date.now(),
        readyAtMs: phase === 'ready_at_boundary' ? Date.now() : null,
        flushStartedAtMs: null,
        flushReason: null
      }
    }
    pendingPlaylistSwapTiming.flushReason = flushReason
    pendingPlaylistSwapTiming.flushStartedAtMs = Date.now()
    if (state.pendingPlaylistSwap != null) {
      setPendingPlaylistSwapFromRoot(
        pending,
        Math.max(state.pendingPlaylistSwap.progress01, phase === 'ready_at_boundary' ? 1 : 0),
        'rematching'
      )
    }
    pokePlaylistSwapDiagnosticsHeartbeat()
    // Keep pendingPlaylistSwap through rematch so hybrid onEnded skips same-URI loops (#1632).
    // Do not clear `deferredRegistrationPlaybackRoot` until apply succeeds — otherwise a throw
    // leaves Feed on the outgoing playlist while the worker hash already matches the new root
    // (no further CAMPAIGN_UPDATE) and Soft-defer never re-arms.
    try {
      // Rematch awaits the ready-gate head only (same as soft-defer ready). Preload stays
      // fire-and-forget; tail is localOnly + loader background fetch. Awaiting preload here
      // blocked first paint while Publicar’s second clip competed with concurrency.
      // Re-resolve head at the cutover instant — do not reuse cached pendingPlaylistHeadUrns
      // (time-block edge at the frame boundary can change the first on-air segment).
      const resolveOpts = playbackMaterializationService.getResolveSegmentOptions()
      const now = getLocalAsUtcInstant()
      const headUrns = collectPendingPlaylistHeadAssetUrns(pending, now, resolveOpts)
      await applyCampaignRegistrationPayloadToPlayback(
        pending,
        headUrns.length > 0 ? { awaitAssetUrns: headUrns } : undefined
      )
      if (deferredRegistrationPlaybackRoot === pending) {
        deferredRegistrationPlaybackRoot = null
        deferredRegistrationPlaybackDeadlineAtMs = 0
      }
      clearPendingPlaylistSwapState({ recordCompleted: true })
      pokePlaylistSwapDiagnosticsHeartbeat()
    }
    catch (err) {
      console.warn('[usePlayer] deferred registration schedule flush failed; will retry', err)
      // Drop rematching latch so the starvation timer can re-enter apply.
      if (state.pendingPlaylistSwap != null && deferredRegistrationPlaybackRoot === pending) {
        const retryPhase
          = pendingPlaylistSwapTiming?.readyAtMs != null ? 'ready_at_boundary' : 'downloading'
        setPendingPlaylistSwapFromRoot(
          pending,
          state.pendingPlaylistSwap.progress01,
          retryPhase
        )
      }
      if (pendingPlaylistSwapTiming != null) {
        pendingPlaylistSwapTiming.flushStartedAtMs = null
        pendingPlaylistSwapTiming.flushReason = null
      }
      armDeferredRegistrationPlaybackFlushTimer({ preserveDeadline: true })
      return
    }
    if (state.tenant?.quotaExcess === true) {
      return
    }
    if (isWeeklyAvailabilitySuppressingPlaybackNow()) {
      return
    }
  }
}

/**
 * Rematerialize when Feed already matches the desired identity but lastSet / on-air queue lag.
 * Shared by CAMPAIGN_UPDATE heal and the hash-stable watchdog (#1636 field stuck).
 *
 * Must not run while soft-defer is armed (prefetch / rematch owns cutover). When it does run,
 * await head URNs only — never `forceAssetReload` (that drops await coalesce and can wait on
 * a full Publicar-scale tail after identity already applied).
 */
async function rematerializeFeedAheadOfMaterialization(
  campaign: IPlaybackScheduleRoot,
  nextId: string
): Promise<void> {
  if (shouldSkipFeedAheadRematerializeWhileSoftDefer({
    deferredRegistrationPlaybackRoot,
    pendingPlaylistSwap: state.pendingPlaylistSwap
  })) {
    return
  }
  // Soft-defer idle — clear any stale defer timer; do not clear pending swap mid-rematch.
  clearDeferredRegistrationPlaybackFlushTimer()
  deferredRegistrationPlaybackRoot = null
  deferredRegistrationPlaybackDeadlineAtMs = 0

  const now = getLocalAsUtcInstant()
  const resolveOpts = playbackMaterializationService.getResolveSegmentOptions()
  const headUrns = collectPendingPlaylistHeadAssetUrns(campaign, now, resolveOpts)
  const awaitOpts
    = headUrns.length > 0 ? { awaitAssetUrns: headUrns } : undefined
  const healStartedAtMs = Date.now()
  pendingPlaylistSwapTiming = {
    armedAtMs: healStartedAtMs,
    readyAtMs: healStartedAtMs,
    flushStartedAtMs: healStartedAtMs,
    flushReason: 'heal'
  }
  pokePlaylistSwapDiagnosticsHeartbeat()
  try {
    const loadedBefore = getPlaybackScheduleMaterializationIdentity(
      playbackMaterializationService.getLastSetPlaybackRoot()
    )
    /**
     * Soft-defer rematch (or a prior apply) already set lastSet to `nextId` while VBuffer
     * queue/seq still lags — re-`setPlaybackSchedule` head-gated; never force-reload the
     * whole campaign (field Campanha→Publicar ~100s).
     */
    if (loadedBefore === nextId) {
      if (state.tenant?.quotaExcess !== true && !isWeeklyAvailabilitySuppressingPlaybackNow()) {
        syncPlaybackAspectRatioFilter()
        await playbackMaterializationService.setPlaybackSchedule(campaign, awaitOpts)
        if (registrationPlaybackResyncHandler != null) {
          await registrationPlaybackResyncHandler()
        }
      }
      return
    }
    await applyCampaignRegistrationPayloadToPlayback(campaign, awaitOpts)
    const stillDesynced
      = getPlaybackScheduleMaterializationIdentity(playbackMaterializationService.getLastSetPlaybackRoot())
        !== nextId
      || getQueueSequenceHash(state.queue) !== playbackMaterializationService.peekPlaybackSequenceHashNow()
    if (
      stillDesynced
      && state.tenant?.quotaExcess !== true
      && !isWeeklyAvailabilitySuppressingPlaybackNow()
    ) {
      syncPlaybackAspectRatioFilter()
      await playbackMaterializationService.setPlaybackSchedule(campaign, awaitOpts)
    }
  }
  finally {
    clearPendingPlaylistSwapState({ recordCompleted: true })
    pokePlaylistSwapDiagnosticsHeartbeat()
  }
}

let feedMaterializationDesyncHealInFlight: Promise<void> | null = null
let feedMaterializationDesyncHealTimer: ReturnType<typeof setInterval> | null = null

/**
 * Soft-defer + CAMPAIGN_UPDATE heal only run on registration hash changes. After Feed flips to the
 * new playlist while lastSet/queue stay on the outgoing one, polls go silent and Slimbox stays on
 * Publicar creatives with Feed “Campanha…” forever. Re-check Feed vs materialization periodically
 * (and on frame boundaries) when no soft-defer is armed.
 *
 * Also: registration snapshot/roots already target Campanha while Feed+lastSet stay on Publicar
 * (worker hash advanced but main never applied — e.g. failed soft-defer flush). Re-enter
 * {@link onRegistrationCampaignUpdate} from the combined roots.
 */
async function healFeedMaterializationDesyncIfNeeded(): Promise<void> {
  if (shouldSkipFeedAheadRematerializeWhileSoftDefer({
    deferredRegistrationPlaybackRoot,
    pendingPlaylistSwap: state.pendingPlaylistSwap
  })) {
    return
  }
  if (state.tenant?.quotaExcess === true || isWeeklyAvailabilitySuppressingPlaybackNow()) {
    return
  }

  const desiredFromRegistration = combineRegistrationScheduleRootsForPlayback(
    state.registrationScheduleRoots,
    getLocalAsUtcInstant(),
    { masterPlaylistUrn: state.assignedMasterPlaylistUrn }
  )
  const desiredId = getPlaybackScheduleMaterializationIdentity(desiredFromRegistration)
  const feedId = getPlaybackScheduleMaterializationIdentity(state.campaign)
  const loadedId = getPlaybackScheduleMaterializationIdentity(
    playbackMaterializationService.getLastSetPlaybackRoot()
  )
  if (
    desiredFromRegistration != null
    && isRegistrationAheadOfPlayback({ desiredId, feedId, loadedId })
  ) {
    if (feedMaterializationDesyncHealInFlight != null) {
      await feedMaterializationDesyncHealInFlight
      return
    }
    feedMaterializationDesyncHealInFlight = (async () => {
      try {
        await onRegistrationCampaignUpdate(desiredFromRegistration)
      }
      finally {
        feedMaterializationDesyncHealInFlight = null
      }
    })()
    await feedMaterializationDesyncHealInFlight
    return
  }

  const campaign = state.campaign
  if (campaign == null) {
    return
  }
  const playingSomething
    = state.queue.length > 0
      && playbackMaterializationService.state === PlaybackScheduleMaterializationState.READY
  if (feedId == null || !playingSomething) {
    return
  }
  const onAirSeq = getQueueSequenceHash(state.queue)
  const peekSeq = playbackMaterializationService.peekPlaybackSequenceHashNow()
  // Treat Feed as the desired target when registration hash is stable (no fresh nextId).
  if (!isFeedAheadOfMaterialization({
    playingSomething,
    uiId: feedId,
    nextId: feedId,
    loadedId,
    onAirSeq,
    peekSeq
  })) {
    return
  }
  if (feedMaterializationDesyncHealInFlight != null) {
    await feedMaterializationDesyncHealInFlight
    return
  }
  feedMaterializationDesyncHealInFlight = (async () => {
    try {
      await rematerializeFeedAheadOfMaterialization(campaign, feedId)
    }
    finally {
      feedMaterializationDesyncHealInFlight = null
    }
  })()
  await feedMaterializationDesyncHealInFlight
}

function ensureFeedMaterializationDesyncHealTimer(): void {
  if (feedMaterializationDesyncHealTimer != null) {
    return
  }
  feedMaterializationDesyncHealTimer = setInterval(() => {
    void healFeedMaterializationDesyncIfNeeded()
  }, FEED_MATERIALIZATION_DESYNC_HEAL_INTERVAL_MS)
}

ensureFeedMaterializationDesyncHealTimer()

async function onRegistrationCampaignUpdate(campaign: IPlaybackScheduleRoot | null): Promise<void> {
  if (campaign === null) {
    /**
     * The registration worker can emit `CAMPAIGN_UPDATE` with `null` when {@link selectFirstCampaignActiveForDate}
     * finds no row for “now”, even while {@link state.registrationScheduleRoots} is still non-empty (brief window
     * while billing merges network operator default into grouped roots). Clearing playback then refilling causes a
     * visible flash on ad-order lines — ignore this transient null while we are still playing and roots still exist.
     */
    const weeklySuppressedNow = isWeeklyAvailabilitySuppressingPlaybackNow()
    const ignoreTransientWorkerNull
      = state.registrationScheduleRoots.length > 0
        && (
          (state.queue.length > 0
            && playbackMaterializationService.state === PlaybackScheduleMaterializationState.READY)
          || weeklySuppressedNow
        )
    if (ignoreTransientWorkerNull) {
      return
    }
    clearDeferredRegistrationPlaybackSchedule()
    await applyCampaignRegistrationPayloadToPlayback(null)
    return
  }
  if (registrationPlaybackDuplicateOfLoaded(campaign)) {
    clearDeferredRegistrationPlaybackSchedule()
    if (!(state.campaign !== null && campaignHash(state.campaign) === campaignHash(campaign))) {
      state.campaign = campaign
    }
    await purgeAgedCachedAssetsProtectingSchedules(campaign)
    void resyncWeeklyAvailabilityPlayback()
    return
  }
  const nextId = getPlaybackScheduleMaterializationIdentity(campaign)
  const loadedId = getPlaybackScheduleMaterializationIdentity(playbackMaterializationService.getLastSetPlaybackRoot())
  const playingSomething
    = state.queue.length > 0
      && playbackMaterializationService.state === PlaybackScheduleMaterializationState.READY
  /**
   * Feed already matches registration but materialization / on-air queue does not
   * (Slimbox: Feed Publicar while Campanha still looping). Force rematerialize.
   * Do not require loadedId === nextId — lastSet can still be the outgoing playlist while
   * `state.campaign` was updated; peek follows lastSet so seq equality alone misses that.
   */
  const uiId = getPlaybackScheduleMaterializationIdentity(state.campaign)
  const onAirSeq = getQueueSequenceHash(state.queue)
  const peekSeq = playbackMaterializationService.peekPlaybackSequenceHashNow()
  const feedAheadOfMaterialization = isFeedAheadOfMaterialization({
    playingSomething,
    uiId,
    nextId,
    loadedId,
    onAirSeq,
    peekSeq
  })
  if (feedAheadOfMaterialization && nextId != null) {
    /**
     * Soft-defer owns cutover for the **same** pending identity — do not rematerialize
     * (historically cleared defer + forceAssetReload on a full Publicar tail).
     * A **different** nextId must fall through so soft-defer can restart prefetch.
     */
    if (shouldSkipFeedAheadRematerializeWhileSoftDefer({
      deferredRegistrationPlaybackRoot,
      pendingPlaylistSwap: state.pendingPlaylistSwap
    })) {
      const pendingId = getPlaybackScheduleMaterializationIdentity(deferredRegistrationPlaybackRoot)
      if (pendingId === nextId || (pendingId == null && state.pendingPlaylistSwap != null)) {
        return
      }
    }
    else {
      await rematerializeFeedAheadOfMaterialization(campaign, nextId)
      return
    }
  }
  const shouldDeferScheduleSwap
    = playingSomething
      && nextId != null
      && (loadedId == null || nextId !== loadedId)
  const loadedRoot = playbackMaterializationService.getLastSetPlaybackRoot()
  const masterJustAssigned
    = campaign != null
      && playbackScheduleRootHasMasterPlaylist(campaign)
      && (loadedRoot == null || !playbackScheduleRootHasMasterPlaylist(loadedRoot))
  if (shouldDeferScheduleSwap && !masterJustAssigned) {
    const pendingId = getPlaybackScheduleMaterializationIdentity(deferredRegistrationPlaybackRoot)
    const samePending = pendingId != null && pendingId === nextId
    if (state.tenant != null) {
      state.campaignUpdateReceivedAt = Date.now()
    }
    /**
     * Feed must stay on the on-air root while soft-deferring. If a prior partial apply already
     * flipped `state.campaign` to the pending identity, snap it back so overlay matches playback.
     */
    const onAirRoot = playbackMaterializationService.getLastSetPlaybackRoot()
    if (
      onAirRoot != null
      && getPlaybackScheduleMaterializationIdentity(state.campaign) !== loadedId
    ) {
      state.campaign = onAirRoot
    }
    // Repeated CAMPAIGN_UPDATE must not reset the 120s starvation ceiling.
    armDeferredRegistrationPlaybackFlushTimer({ preserveDeadline: samePending })
    const swap = state.pendingPlaylistSwap
    if (shouldRestartPendingPlaylistPrefetch({
      samePending,
      hasSwapState: swap != null
    })) {
      deferredRegistrationPlaybackRoot = campaign
      startPendingPlaylistPrefetch(campaign)
    }
    else if (swap != null && deferredRegistrationPlaybackRoot != null) {
      // Keep the same object reference so in-flight prefetch `!== root` checks stay alive.
      setPendingPlaylistSwapFromRoot(
        deferredRegistrationPlaybackRoot,
        swap.progress01,
        swap.phase
      )
    }
    else {
      deferredRegistrationPlaybackRoot = campaign
      startPendingPlaylistPrefetch(campaign)
    }
    await purgeAgedCachedAssetsProtectingSchedules(campaign, playbackMaterializationService.getLastSetPlaybackRoot())
    return
  }
  if (shouldDeferScheduleSwap && masterJustAssigned) {
    if (state.tenant != null) {
      state.campaignUpdateReceivedAt = Date.now()
    }
    await purgeAgedCachedAssetsProtectingSchedules(campaign, playbackMaterializationService.getLastSetPlaybackRoot())
    clearDeferredRegistrationPlaybackSchedule()
    await applyCampaignRegistrationPayloadToPlayback(campaign)
    return
  }
  clearDeferredRegistrationPlaybackSchedule()
  await applyCampaignRegistrationPayloadToPlayback(campaign)
}

registrationStatusService.on(RegistrationStatusServiceEvent.CAMPAIGN_UPDATE, (campaign) => {
  void onRegistrationCampaignUpdate(campaign)
})

registrationStatusService.on(RegistrationStatusServiceEvent.NEXT_CAMPAIGN_SCHEDULED, (campaign) => {
  state.nextCampaign = campaign
})

playerUpdaterService.on(PlayerUpdateServiceEvent.UPDATE_AVAILABLE, ({ version }) => {
  state.update = { version, status: 'available' }
  // Ensure install-from-unknown-sources permission is requested when an update is available (no-op if already granted or asked recently).
  void (async () => {
    if (getPlatform() !== 'android' || await isAndroidPlayStoreDistributionResolved()) {
      return
    }
    void import('@tauri-apps/api/core').then(({ invoke }) =>
      invoke('plugin:lucimark-android|requestInstallUnknownAppsPermissionIfNeeded').catch(() => {})
    )
  })()
})

playerUpdaterService.on(PlayerUpdateServiceEvent.DOWNLOAD, ({ version, progress }) => {
  state.update = { version, status: 'downloading', download: progress }
})

playerUpdaterService.on(PlayerUpdateServiceEvent.DOWNLOAD_COMPLETE, ({ version, installPendingRestart }) => {
  state.update = {
    version,
    status: installPendingRestart === true ? 'complete_pending_restart' : 'complete'
  }
})

playerUpdaterService.on(PlayerUpdateServiceEvent.DOWNLOAD_FAILED, ({ version, error }) => {
  state.update = { version, status: 'failed', error: error ?? 'Unknown error' }
})

if (getPlatform() === 'android') {
  void import('@tauri-apps/api/event').then(({ listen }) => {
    listen<{ version: string, progress: number }>('updater-download-progress', (event) => {
      const { version, progress } = event.payload
      void playerUpdaterService.emit(PlayerUpdateServiceEvent.DOWNLOAD, { version, progress })
    }).catch(() => {})
  }).catch(() => {})
}

/** Last webOta from WebSocket handshake; used when registered and registration-status never included `webOta`. */
let lastHandshakeWebOta: IWebOtaUpdateEventPayload | null = null

/**
 * Last `webOta` from registration-status when the JSON included the key (`object` | `null`).
 * When a later poll/offline snapshot omits `webOta`, keep using this instead of a stale WS handshake offer.
 * `undefined` = no explicit registration-status web OTA seen since last full reset.
 */
let lastExplicitRegistrationStatusWebOta: IWebOtaUpdateEventPayload | null | undefined

/**
 * Applies Web OTA / browser reload when allowed by tenant `webOtaApplyTiming`.
 * Unregistered players: pass timing undefined (always immediate).
 */
function applyWebOtaIfAllowed(
  webOta: IWebOtaUpdateEventPayload | null | undefined,
  timing: TWebOtaApplyTiming | undefined
): void {
  if (!shouldAllowWebOtaAutoApplyNow(timing)) {
    logger.debug('Web OTA: skipped (deferred until next app restart)')
    return
  }
  void checkAndApplyWebOta(webOta)
  void reloadWebBrowserForNewWebOta(webOta)
}

/** Resolves which web OTA to apply: explicit payload key, else last explicit registration-status value, else handshake. */
function getWebOtaToApply(payload: IPlayerRegistrationPayload): IWebOtaUpdateEventPayload | null {
  if (payload.webOta !== undefined) {
    return payload.webOta
  }
  if (payload.tenant != null && lastExplicitRegistrationStatusWebOta !== undefined) {
    return lastExplicitRegistrationStatusWebOta
  }
  if (payload.tenant != null && lastHandshakeWebOta !== null) {
    return lastHandshakeWebOta
  }
  return null
}

/** True when running inside Tauri (Android or desktop). Web OTA only runs in Tauri. */
function isTauriContext(): boolean {
  return isTauriRuntime()
}

/**
 * Skip browser hard-reload on web OTA id mismatch (web only) so DevTools Network / WS payloads stay inspectable.
 * - URL: `?lucimark_debug_skip_web_ota_reload=1` (or `true`)
 * - localStorage: `lucimark_debug_skip_web_ota_reload` = `1`
 */
const DEBUG_SKIP_WEB_OTA_BROWSER_RELOAD_KEY = 'lucimark_debug_skip_web_ota_reload'

/**
 * Set before `location.replace` with `_web_ota=`. Tracks per-offered-version reload attempts;
 * see {@link webOtaBrowserReloadSession}.
 */

/** Same-document: after we abort a reload loop, ignore further mismatch calls (e.g. duplicate bootstrap). */
let webOtaBrowserReloadAbortedThisDocument = false

/**
 * Bumped when bundle matches offer, server withdraws OTA, we abort cache-bust, or a newer reload is
 * scheduled. In-flight `reloadWebBrowserForNewWebOta` compares its snapshot after `await` — if the
 * epoch moved, it must not call `location.replace` (avoids `_web_ota` loops when `offeredWebOtaVersion`
 * is null or races with registration).
 */
let webOtaBrowserHardReloadEpoch = 0

function shouldDebugSkipWebOtaBrowserReload(): boolean {
  if (typeof window === 'undefined') { return false }
  try {
    const q = new URL(window.location.href).searchParams.get(DEBUG_SKIP_WEB_OTA_BROWSER_RELOAD_KEY)
    if (q === '1' || q === 'true') { return true }
  }
  catch {
    // ignore
  }
  try {
    if (window.localStorage?.getItem(DEBUG_SKIP_WEB_OTA_BROWSER_RELOAD_KEY) === '1') { return true }
  }
  catch {
    // ignore
  }
  return false
}

function replaceUrlWithoutWebOtaQueryParamIfPresent(): void {
  if (typeof window === 'undefined') { return }
  try {
    const u = new URL(window.location.href)
    if (!u.searchParams.has('_web_ota')) { return }
    u.searchParams.delete('_web_ota')
    const qs = u.searchParams.toString()
    const path = `${u.pathname}${qs !== '' ? `?${qs}` : ''}${u.hash}`
    window.history.replaceState({}, '', path.length > 0 ? path : u.pathname)
  }
  catch {
    // ignore
  }
}

/**
 * Browser player: when the API offers a web OTA whose version differs from this build’s embedded
 * bundle id (git hash), hard-reload with a cache-busting query param. The host must deploy the same
 * bundle that was packed for that OTA (e.g. Pages/worker static deploy). No-op on Tauri/Android.
 */
async function reloadWebBrowserForNewWebOta(webOta: IWebOtaUpdateEventPayload | null | undefined): Promise<void> {
  if (typeof window === 'undefined') { return }
  if (getPlatform() !== 'web' || isTauriContext()) { return }
  if (webOta == null || webOta.version == null || String(webOta.version).trim() === '') {
    webOtaBrowserHardReloadEpoch++
    return
  }
  const localRaw = await Promise.resolve(getWebBundleVersion())
  if (localRaw == null || localRaw === '') { return }
  const local = normalizeWebOtaVersionToken(localRaw)
  const offered = normalizeWebOtaVersionToken(webOta.version)
  if (local === '' || offered === '') { return }
  if (local.toLowerCase() === offered.toLowerCase()) {
    webOtaBrowserHardReloadEpoch++
    clearWebOtaBrowserReloadSession()
    replaceUrlWithoutWebOtaQueryParamIfPresent()
    return
  }

  if (webOtaBrowserReloadAbortedThisDocument) { return }

  const reloadAttempts = getWebOtaBrowserReloadAttempts(offered)
  if (reloadAttempts >= WEB_OTA_BROWSER_MAX_RELOAD_ATTEMPTS) {
    webOtaBrowserHardReloadEpoch++
    webOtaBrowserReloadAbortedThisDocument = true
    logger.error(
      {
        localBundleId: localRaw,
        offeredVersion: webOta.version,
        normalizedLocal: local,
        normalizedOffered: offered,
        reloadAttempts
      },
      'Web OTA: bundle still mismatches API after reload attempts. Skipping further browser reloads — fix Pages/API deploy skew or edge cache. Use ?lucimark_debug_skip_web_ota_reload=1 to inspect.'
    )
    replaceUrlWithoutWebOtaQueryParamIfPresent()
    return
  }

  const escalateUnregister = reloadAttempts >= 1

  const webOtaSafe = { version: webOta.version, key: webOta.key, minAppVersion: webOta.minAppVersion }
  logger.warn(
    {
      localBundleId: localRaw,
      offeredVersion: webOta.version,
      normalizedLocal: local,
      normalizedOffered: offered,
      webOta: webOtaSafe,
      reloadAttempts,
      escalateUnregister
    },
    escalateUnregister
      ? 'Web OTA: bundle id mismatch — scheduling browser reload with service worker unregister (escalation)'
      : 'Web OTA: bundle id mismatch — scheduling browser reload (jwt not logged). Use ?lucimark_debug_skip_web_ota_reload=1 or localStorage lucimark_debug_skip_web_ota_reload=1 to skip reload and inspect registration-status / WS.'
  )

  if (shouldDebugSkipWebOtaBrowserReload()) {
    webOtaBrowserHardReloadEpoch++
    logger.warn('Web OTA: skipped browser reload (debug flag). Clear flag when done debugging.')
    return
  }

  const scheduledHardReloadEpoch = ++webOtaBrowserHardReloadEpoch

  incrementWebOtaBrowserReloadAttempts(offered)

  try {
    await prepareBrowserWebOtaReload({ escalateUnregister })
  }
  catch {
    // still reload — primary goal is fresh HTML/JS from origin
  }

  if (scheduledHardReloadEpoch !== webOtaBrowserHardReloadEpoch) {
    rollbackWebOtaBrowserReloadAttempt(offered)
    replaceUrlWithoutWebOtaQueryParamIfPresent()
    logger.debug('Web OTA: skipped stale browser reload — superseded (match, abort, newer schedule, or OTA withdrawn)')
    return
  }

  // A newer registration / WS event may have already matched the bundle and stripped `_web_ota`
  // while we awaited SW cache work. Reloading anyway re-adds `_web_ota` and can loop forever.
  const authoritative = state.offeredWebOtaVersion
  if (authoritative != null && String(authoritative).trim() !== '') {
    const localNow = normalizeWebOtaVersionToken(String(await Promise.resolve(getWebBundleVersion()) ?? ''))
    const offNow = normalizeWebOtaVersionToken(String(authoritative))
    if (localNow !== '' && offNow !== '' && localNow.toLowerCase() === offNow.toLowerCase()) {
      clearWebOtaBrowserReloadSession()
      replaceUrlWithoutWebOtaQueryParamIfPresent()
      logger.debug(
        { localBundleId: localNow, offeredVersion: authoritative },
        'Web OTA: skipped stale browser reload — bundle already matches latest offered version'
      )
      return
    }
  }

  const u = new URL(window.location.href)
  u.searchParams.set('_web_ota', String(Date.now()))
  window.location.replace(u.toString())
}

/** Server-authoritative web OTA: apply latest from registration-status, or revert to built-in when server no longer offers an OTA. Runs only on Tauri (Android/desktop). */
async function checkAndApplyWebOta(webOta: IWebOtaUpdateEventPayload | null | undefined): Promise<void> {
  if (getPlatform() === 'web' && !isTauriContext()) { return }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const current = await invoke<string | null>('ota_web_get_current_version')

    if (!webOta) {
      // Revert when we have a bundle and server offered no OTA. When registration-status explicitly sends webOta: null, setRegistrationStatus clears lastHandshakeWebOta so we revert; when the key is omitted (race), lastHandshakeWebOta stays set and we do not revert.
      if (current != null && current !== '' && lastHandshakeWebOta === null) {
        await invoke('ota_web_revert')
        logger.info('Web OTA: reverted to embedded bundle; resetting service worker before reload')
        // Widget SW uses stale-while-revalidate for same-origin navigations + static assets; without reset,
        // reload can serve cached index.html / old hashed chunks while disk already changed → 404/504 and a stuck boot splash.
        try {
          await resetServiceWorkerForNewWebBundle()
        }
        catch {
          // still navigate
        }
        // Cache-bust so the WebView fetches fresh from localhost (embedded bundle); avoids needing a second full app restart to see rollback.
        const u = new URL(window.location.href)
        u.searchParams.set('_ota_revert', String(Date.now()))
        window.location.href = u.toString()
        return
      }
      return
    }

    if (current === webOta.version) { return }
    const trace = webOta.otaTraceId?.trim()
    addPlayerApkOtaSentryBreadcrumb({
      phase: 'web_ota_start_download',
      offeredVersion: webOta.version,
      ...(trace != null && trace !== '' ? { otaTraceId: trace } : {})
    })
    const base = getApiURL().replace(/\/$/, '')
    const playerId = await authenticationService.getPlayerId()
    const b64PlayerId = btoa(playerId)
    const url = `${base}/players/${b64PlayerId}/updates/${webOta.jwt}`
    const response = await fetch(url)
    if (!response.ok) { throw new Error(`download failed: ${response.status}`) }
    const body = response.body
    if (!body) { throw new Error('no response body') }
    const reader = body.getReader()
    await invoke('ota_web_begin_download')
    function uint8ArrayToBase64(bytes: Uint8Array): string {
      let binary = ''
      for (let i = 0; i < bytes.length; i++) { binary += String.fromCharCode(bytes[i]!) }
      return btoa(binary)
    }
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) { break }
        if (value?.length) { await invoke('ota_web_append_chunk', { chunkBase64: uint8ArrayToBase64(value) }) }
      }
      await invoke('ota_web_finish_download', { version: webOta.version })
      logger.info({ version: webOta.version }, 'Web OTA: bundle installed; resetting service worker before reload')
      try {
        await resetServiceWorkerForNewWebBundle()
      }
      catch {
        // still reload
      }
      window.location.reload()
    }
    finally {
      reader.releaseLock()
    }
  }
  catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    const trace = webOta?.otaTraceId?.trim()
    addPlayerApkOtaSentryBreadcrumb({
      phase: 'web_ota_check_apply_failed',
      error: message,
      ...(trace != null && trace !== '' ? { otaTraceId: trace } : {}),
      ...(webOta != null ? { offeredVersion: webOta.version } : {})
    })
    logger.warn(`Web OTA check/apply failed: ${message}`)
  }
}

type TSetRegistrationStatusOptions = {
  /** Defaults to `live`. Cached applications must not re-save the snapshot (preserves true age). */
  source?: 'live' | 'cached'
  /** Required for accurate age when `source === 'cached'`; otherwise loaded from IDB. */
  snapshotSavedAtMs?: number | null
}

const setRegistrationStatus = async (
  payload: IPlayerRegistrationPayload | null,
  options: TSetRegistrationStatusOptions = {}
) => {
  const source = options.source ?? 'live'
  syncOperatorMasterPlaylistUrnFromRegistration(payload)
  if (payload === null) {
    // Drop Sync cue immediately on unregister / cleared status (do not wait for min-visible timer).
    clearRegistrationStatusRefreshPendingNow()
    clearDeferredRegistrationPlaybackSchedule()
    resetWebOtaSessionGate()
    state.availabilityWeeklySchedule = null
    state.proofOfPlayOrderId = null
    state.ctaRegistry = {}
    state.venueId = null
    state.tenantHandle = null
    state.registrationConfigurationSource = 'live'
    state.registrationSnapshotSavedAtMs = null
    await dbService.clearRegistrationStatusSnapshot()
    state.offeredWebOtaVersion = null
    lastExplicitRegistrationStatusWebOta = undefined
    lastAppliedOverlayUiDirectiveSeq = 0
    lastAppliedAssetCacheRepairDirectiveSeq = 0
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(OVERLAY_UI_DIRECTIVE_SEQ_KEY)
        window.localStorage.removeItem(ASSET_CACHE_REPAIR_DIRECTIVE_SEQ_KEY)
      }
    }
    catch {
      // ignore
    }
  }
  else {
    if (payload.serverTimeMs != null && Number.isFinite(payload.serverTimeMs)) {
      const nextOffset = payload.serverTimeMs - Date.now()
      const prevOffset = state.serverClockOffsetMs
      if (prevOffset === 0 || Math.abs(nextOffset - prevOffset) >= 200) {
        state.serverClockOffsetMs = nextOffset
      }
    }
    // Show a small top-left blip when we receive registration-status while still unregistered.
    if (state.tenant === null) {
      state.registrationStatusReceivedAt = Date.now()
    }
    if ('webOta' in payload) {
      lastExplicitRegistrationStatusWebOta = payload.webOta ?? null
    }
    // Explicit "no OTA" from server: treat as authoritative so we revert when we have a bundle; clear handshake fallback so revert runs.
    if ('webOta' in payload && payload.webOta === null) {
      lastHandshakeWebOta = null
    }
    const webOtaToApply = getWebOtaToApply(payload)
    state.offeredWebOtaVersion = webOtaToApply?.version ?? null
    applyWebOtaIfAllowed(webOtaToApply, payload.tenant?.webOtaApplyTiming)
    applyRemoteOverlayUiFromPayload(payload)
    void applyAssetCacheRepairDirectiveIfNeeded(payload.assetCacheRepairDirectiveSeq)
    state.availabilityWeeklySchedule = parsePlayerWeeklyAvailabilitySchedule(payload.availabilityWeeklySchedule ?? null)
    state.proofOfPlayOrderId = payload.proofOfPlayOrderId ?? null
    if (payload.ctaRegistry != null) {
      state.ctaRegistry = payload.ctaRegistry
    }
    state.venueId = payload.venueId ?? null
    state.tenantHandle = typeof payload.tenantHandle === 'string' && payload.tenantHandle.trim() !== ''
      ? payload.tenantHandle.trim()
      : null
  }
  registrationStatusService.setRegistrationStatus(payload)
  syncPlayerSentryContext(payload)
  if (payload !== null) {
    if (source === 'cached') {
      state.registrationConfigurationSource = 'cached'
      state.registrationSnapshotSavedAtMs
        = options.snapshotSavedAtMs
          ?? await dbService.getRegistrationStatusSnapshotSavedAtMs()
      // Do not re-save — that would reset savedAtMs and hide how stale the offline config is.
    }
    else {
      state.registrationConfigurationSource = 'live'
      await dbService.saveRegistrationStatusSnapshot(payload)
      state.registrationSnapshotSavedAtMs = await dbService.getRegistrationStatusSnapshotSavedAtMs()
    }
  }
}

authenticationService.on(AuthenticationServiceEvent.REGISTRATION, async (registration) => {
  state.registrationCode = authenticationService.registrationCode
  if (registration != null) {
    state.registrationBootstrapError = null
  }
  if (registration != null && registration.tenantId != null) {
    // Tenant credentials restored — drop sticky pairing shell flags (#1438).
    state.pairingAfterUnregisterPending = false
    state.registrationCode = null
  }
  else if (registration != null && registration.tenantId === null) {
    state.pairingAfterUnregisterPending = false
  }
  if (registration === null && state.tenant != null) {
    state.pairingAfterUnregisterPending = true
  }
  if (!registration?.tenantId) {
    state.campaignUpdateReceivedAt = 0
    await setRegistrationStatus(null)
    await playbackMaterializationService.setPlaybackSchedule(null)
    // Show top-left blip when we received a registration response from the server (GET /registration poll), not when we cleared or hydrated from storage.
    if (registration != null && !suppressUnregisteredRegistrationStatusBlip) {
      state.registrationStatusReceivedAt = Date.now()
    }
  }
  if (registration) {
    const resolvedAppVersion = await syncStateAppVersion()
    if (resolvedAppVersion !== '' && resolvedAppVersion !== registration.version) {
      try {
        await apiClientService.reportVersionUpdate(resolvedAppVersion)
      }
      catch {
        // Non-fatal (transient network, e.g. WebKit "Load failed"): a later REGISTRATION
        // handler or heartbeat retries; rejecting here would bubble through Emittery emit
        // to fire-and-forget setRegistration callers (Sentry PLAYER-10).
      }
    }
  }
})

/** Max retries for registration-status fetch after a push (transient network errors). */
const REGISTRATION_STATUS_FETCH_RETRIES = 3
/** Delay (ms) before each retry: 1s, 2s, 4s. */
const REGISTRATION_STATUS_FETCH_RETRY_DELAYS_MS = [1000, 2000, 4000]

async function fetchRegistrationStatusSingleWithSoftRetry(): Promise<IPlayerRegistrationPayload | null> {
  return apiClientService.fetchRegistrationStatusWithStaleCredentialRecovery()
}

async function applyRegistrationUnauthorizedPolicy(err: ApiClientServiceUnauthorizedError, context: string): Promise<'cleared' | 'retained'> {
  const status = err.httpStatus ?? 401
  const code = err.code
  const decision = shouldClearPersistedRegistration({ code, status })
  if (decision === 'no_clear') {
    logger.info({ context, code, status }, 'retained persisted registration after auth error')
    return 'retained'
  }
  if (decision === 'clear') {
    logger.warn({ context, code, status }, 'server revoke auth code; checking Players Directory before clear')
    const revokeGate = await registrationClearDirectoryGate('revoke')
    if (revokeGate !== 'proceed_clear') {
      logger.info({ context, code, status, gate: revokeGate }, 'skipped server-revoke registration clear (directory gate)')
      return 'retained'
    }
    logger.warn({ context, code, status }, 'clearing persisted registration (server revoke code)')
    reportRegistrationClearedToSentry({
      reason: 'server_revoke',
      source: 'http',
      context,
      code,
      httpStatus: status
    })
    await authenticationService.setRegistration(null, {
      registrationBackup: registrationBackupDispositionForPersistedClear('server_revoke')
    })
    return 'cleared'
  }
  const streak = recordPlayerAuthAmbiguousFailure('http')
  logger.warn({ context, code, status, streak }, 'ambiguous player auth failure for registration')
  if (streak) {
    logger.warn({ context, code, status }, 'ambiguous auth streak threshold reached; checking Players Directory before clear')
    const gate = await registrationClearDirectoryGate('ambiguous')
    if (gate !== 'proceed_clear') {
      logger.info({ context, code, status, gate }, 'skipped ambiguous registration clear (directory gate)')
      return 'retained'
    }
    if (!tryAcquireAmbiguousClearCooldown()) {
      logger.info({ context, code, status }, 'skipped ambiguous registration clear (cooldown active)')
      return 'retained'
    }
    logger.warn({ context, code, status }, 'clearing persisted registration after ambiguous auth streak')
    reportRegistrationClearedToSentry({
      reason: 'ambiguous_streak',
      source: 'http',
      context,
      code,
      httpStatus: status
    })
    await authenticationService.setRegistration(null, {
      registrationBackup: registrationBackupDispositionForPersistedClear('ambiguous_streak')
    })
    resetPlayerAuthAmbiguousFailureStreak()
    return 'cleared'
  }
  return 'retained'
}
/** Delay (ms) before setPlayerOffline when transitioning to DISCONNECTED so any in-flight registration message (e.g. server pushed registration then closed) is applied first; avoids 403 invalid player registration from stale credentials. */
const OFFLINE_DELAY_AFTER_DISCONNECT_MS = 150
/** Min time (ms) to wait after a registration was applied (e.g. via WebSocket) before sending PATCH offline; extra safeguard so credentials are never stale. */
const OFFLINE_MIN_DELAY_AFTER_REGISTRATION_MS = 1000

/** Timestamp (ms) when registration was last applied (e.g. from WebSocket push or GET registration). Used to extend offline delay when disconnect happens right after registration. */
let lastRegistrationAppliedAt = 0
/** True when we intentionally disconnected to migrate from PlayersDirectoryDO to TenantDO; skip PATCH offline so the app does not show the player offline during reconnect. */
let reconnectingToTenant = false

/**
 * Calls PATCH offline after a safe delay so we never send stale credentials (avoids 403 invalid player registration).
 * Use from all call sites that report offline (WebSocket disconnect, visibility change, app state inactive).
 */
async function reportOfflineWithSafeDelay(): Promise<void> {
  await new Promise(resolve => setTimeout(resolve, OFFLINE_DELAY_AFTER_DISCONNECT_MS))
  const elapsed = Date.now() - lastRegistrationAppliedAt
  if (elapsed < OFFLINE_MIN_DELAY_AFTER_REGISTRATION_MS && lastRegistrationAppliedAt > 0) {
    await new Promise(resolve => setTimeout(resolve, OFFLINE_MIN_DELAY_AFTER_REGISTRATION_MS - elapsed))
  }
  try {
    await apiClientService.setPlayerOffline()
  }
  catch {
    // ignore — observability-only
  }
}

const fetchRegistrationStatusWithRetry = async (): Promise<IPlayerRegistrationPayload | null> => {
  let lastErr: unknown
  for (let attempt = 0; attempt <= REGISTRATION_STATUS_FETCH_RETRIES; attempt++) {
    try {
      const result = await fetchRegistrationStatusSingleWithSoftRetry()
      resetPlayerAuthAmbiguousFailureStreak()
      return result
    }
    catch (err) {
      lastErr = err
      if (err instanceof ApiClientServiceUnauthorizedError) {
        throw err
      }
      if (attempt < REGISTRATION_STATUS_FETCH_RETRIES) {
        const delayMs = REGISTRATION_STATUS_FETCH_RETRY_DELAYS_MS[attempt] ?? 4000
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    }
  }
  throw lastErr
}

/**
 * Debounce window for WS `registration-status-update` bursts (e.g. master assign → setPlayerMaster + replaceExclusive).
 * Keep short: status overlay shows {@link state.registrationStatusRefreshPending} immediately on push;
 * this only delays the GET so assign bursts can settle.
 */
const REGISTRATION_STATUS_PUSH_DEBOUNCE_MS = 100
const REGISTRATION_STATUS_PUSH_DEBOUNCE_MAX_WAIT_MS = 400
/** Minimum time “Recebendo” stays on-screen so the cue is readable (fetch often finishes in <200ms locally). */
const REGISTRATION_STATUS_REFRESH_MIN_VISIBLE_MS = 2200
/** Extra hold after min-visible for CAMPAIGN_UPDATE / soft-defer to own the same Org row (no blank gap). */
const REGISTRATION_STATUS_REFRESH_SETTLE_GRACE_MS = 600
/** Hard cap so a stuck worker cannot pin “Recebendo” forever. */
const REGISTRATION_STATUS_REFRESH_HOLD_MAX_MS = 45_000
/** Poll interval while waiting for soft-defer handoff or settle grace. */
const REGISTRATION_STATUS_REFRESH_SETTLE_POLL_MS = 200

let registrationStatusRefreshStartedAtMs = 0
let registrationStatusRefreshClearTimer: ReturnType<typeof setTimeout> | null = null
let registrationStatusRefreshGeneration = 0

function clearRegistrationStatusRefreshClearTimer(): void {
  if (registrationStatusRefreshClearTimer != null) {
    clearTimeout(registrationStatusRefreshClearTimer)
    registrationStatusRefreshClearTimer = null
  }
}

/** Drop receiving cue when soft-defer owns the same busy latch (or after settle / unregister). */
function clearRegistrationStatusRefreshPendingNow(): void {
  clearRegistrationStatusRefreshClearTimer()
  state.registrationStatusRefreshPending = false
  registrationStatusRefreshStartedAtMs = 0
}

function isLiveRegistrationAheadOfPlayback(): boolean {
  return isRegistrationScheduleAheadOfOnAir({
    roots: state.registrationScheduleRoots,
    assignedMasterPlaylistUrn: state.assignedMasterPlaylistUrn,
    campaign: state.campaign,
    loadedRoot: playbackMaterializationService.getLastSetPlaybackRoot(),
    now: getLocalAsUtcInstant()
  })
}

function scheduleRegistrationStatusRefreshPendingClear(generation: number): void {
  clearRegistrationStatusRefreshClearTimer()
  const startedAt = registrationStatusRefreshStartedAtMs
  const remainMs = startedAt > 0
    ? Math.max(0, REGISTRATION_STATUS_REFRESH_MIN_VISIBLE_MS - (Date.now() - startedAt))
    : 0

  const trySettle = (): void => {
    registrationStatusRefreshClearTimer = null
    if (generation !== registrationStatusRefreshGeneration) {
      return
    }
    const elapsedMs = startedAt > 0 ? Date.now() - startedAt : REGISTRATION_STATUS_REFRESH_HOLD_MAX_MS
    const decision = shouldClearContentChangeReceivingCue({
      hasPendingPlaylistSwap: state.pendingPlaylistSwap != null,
      hasDeferredRegistrationRoot: deferredRegistrationPlaybackRoot != null,
      registrationAheadOfPlayback: isLiveRegistrationAheadOfPlayback(),
      elapsedMs,
      minVisibleMs: REGISTRATION_STATUS_REFRESH_MIN_VISIBLE_MS,
      settleGraceMs: REGISTRATION_STATUS_REFRESH_SETTLE_GRACE_MS,
      holdMaxMs: REGISTRATION_STATUS_REFRESH_HOLD_MAX_MS
    })
    if (decision === 'clear') {
      state.registrationStatusRefreshPending = false
      registrationStatusRefreshStartedAtMs = 0
      return
    }
    registrationStatusRefreshClearTimer = setTimeout(trySettle, REGISTRATION_STATUS_REFRESH_SETTLE_POLL_MS)
  }

  registrationStatusRefreshClearTimer = setTimeout(trySettle, remainMs)
}

const refetchRegistrationStatusAfterPush = createRegistrationStatusPushRefetchCoalescer(async () => {
  try {
    const registrationStatus = await fetchRegistrationStatusWithRetry()
    await setRegistrationStatus(registrationStatus)
  }
  catch (err) {
    let authCleared = false
    if (err instanceof ApiClientServiceUnauthorizedError) {
      authCleared = (await applyRegistrationUnauthorizedPolicy(err, 'registration_status_push')) === 'cleared'
    }
    if (!authCleared) {
      const cachedRow = await dbService.getRegistrationStatusSnapshotRecord()
      if (cachedRow !== null) {
        logger.warn({ err }, 'registration-status refetch failed; applying cached registration snapshot')
        await setRegistrationStatus(cachedRow.payload, {
          source: 'cached',
          snapshotSavedAtMs: cachedRow.savedAtMs
        })
      }
      else if (!(err instanceof ApiClientServiceUnauthorizedError)) {
        logger.warn({ err }, 'registration-status refetch failed; no cached snapshot')
      }
    }
  }
  finally {
    // Capture generation at end so coalesced callers that bumped mid-flight still hold receiving cue.
    scheduleRegistrationStatusRefreshPendingClear(registrationStatusRefreshGeneration)
  }
})

// Trailing debounce + maxWait: coalesce rapid WS pushes and spaced assign steps (setPlayerMaster then replaceExclusive).
const debouncedOnPlayerRegistrationStatusUpdate = debounce(
  () => void refetchRegistrationStatusAfterPush(),
  REGISTRATION_STATUS_PUSH_DEBOUNCE_MS,
  { leading: false, trailing: true, maxWait: REGISTRATION_STATUS_PUSH_DEBOUNCE_MAX_WAIT_MS }
)

function onPlayerRegistrationStatusUpdateFromWs(): void {
  // ASAP operator feedback in CurrentState — do not wait for debounce / GET.
  registrationStatusRefreshGeneration++
  clearRegistrationStatusRefreshClearTimer()
  if (!state.registrationStatusRefreshPending) {
    registrationStatusRefreshStartedAtMs = Date.now()
  }
  state.registrationStatusRefreshPending = true
  debouncedOnPlayerRegistrationStatusUpdate()
}

let showMeOverlayTimer: ReturnType<typeof setTimeout> | null = null
function triggerShowMeOverlay(title: string, durationSeconds: number) {
  if (showMeOverlayTimer != null) {
    clearTimeout(showMeOverlayTimer)
    showMeOverlayTimer = null
  }
  state.showMeOverlay.title = title
  state.showMeOverlay.visible = true
  showMeOverlayTimer = setTimeout(() => {
    state.showMeOverlay.visible = false
    state.showMeOverlay.title = ''
    showMeOverlayTimer = null
  }, durationSeconds * 1000)
}

const audienceWebRtcPreviewSession = createAudienceWebRtcPreviewSession({
  postSignal: body => apiClientService.postAudienceWebRtcSignal(body),
  isPreviewEnabled: () => state.tenant?.audienceWebcamPreview === true,
  getVideoInputDeviceId: () => state.audienceVideoInputDeviceId,
  getIceServers: () => state.tenant?.audienceWebRtcIceServers ?? []
})
getAudienceWebRtcPreviewActive = () => audienceWebRtcPreviewSession.isPreviewActive()

const audienceAnalyticsMotionPipeline = createAudienceAnalyticsMotionPipeline({
  shouldRun: () =>
    supportsAudienceCameraSurface()
    && state.tenant?.audienceAnalytics === true
    && state.audienceAnalyticsEnabled
    && !state.lowMemoryMode
    && state.capacityTier !== 'low',
  // Person-CV: same `capacityTier !== 'low'` gate as `shouldRun` so Android is not stuck with motion on but CV off while tier is still null.
  personCvEnabled: () =>
    supportsAudienceCameraSurface()
    && state.tenant?.audienceAnalytics === true
    && state.tenant?.audiencePersonCvAnalytics === true
    && state.audienceAnalyticsEnabled
    && !state.lowMemoryMode
    && state.capacityTier !== 'low',
  isWebRtcPreviewActive: () => audienceWebRtcPreviewSession.isPreviewActive(),
  getVideoInputDeviceId: () => state.audienceVideoInputDeviceId,
  getCapacityTier: () => state.capacityTier,
  onLiveStats: (snapshot) => {
    state.audienceAnalyticsLive = snapshot
  }
})

watch(
  () =>
    [
      state.tenant?.audienceAnalytics,
      state.tenant?.audiencePersonCvAnalytics,
      state.audienceAnalyticsEnabled,
      state.lowMemoryMode,
      state.capacityTier,
      state.platform
    ] as const,
  () => {
    audienceAnalyticsMotionPipeline.sync()
  },
  { immediate: true }
)

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  }
  catch {
    return null
  }
}

const brightnessAutomation = createBrightnessAutomation({
  apply: applyAndroidBrightness,
  applyAutomatic: applyAndroidAutomaticBrightness,
  // Refresh Studio's reported brightness right away instead of waiting for the next heartbeat.
  onApplied: () => {
    void reportPlaybackHeartbeatWithProofOfPlay()
  },
  startSensor: startAndroidAmbientLight,
  stopSensor: stopAndroidAmbientLight,
  readLux: async () => (await fetchAndroidAmbientLightStatus())?.lux ?? null,
  storage: safeLocalStorage()
})

const webSocketMessageProcessor = new WebSocketMessageProcessor({
  onPlayerRegistration: async (registration) => {
    await authenticationService.setRegistration(registration)
    resetPlayerAuthAmbiguousFailureStreak()
    lastRegistrationAppliedAt = Date.now()
  },
  onPlayerRegistrationStatusUpdate: onPlayerRegistrationStatusUpdateFromWs,
  onPlayerUnregistered: () => handleTenantUnregisteredByServer('player_unregistered_ws'),
  onRequestPlaybackHeartbeat: () => {
    invalidateAudienceVideoInputDevicesCache()
    void reportPlaybackHeartbeatWithProofOfPlay()
  },
  playerResetHandler: async () => {
    audienceAnalyticsMotionPipeline.dispose()
    state.audienceAnalyticsLastSentAggregate = null
    await audienceWebRtcPreviewSession.teardown()
    await playerUpdaterService.reset()
  },
  playerUpdateHandler: u =>
    playerUpdaterService.update(u, { webOtaApplyTiming: state.tenant?.webOtaApplyTiming }),
  screenRotationHandler: (rotation) => {
    screen.rotation = rotation
  },
  setScreenBrightnessHandler: ({ percent, auto }) => {
    void brightnessAutomation.setConfig({ percent, auto: auto ?? null })
  },
  showMeOverlayHandler: (payload) => {
    triggerShowMeOverlay(payload.title, payload.durationSeconds)
  },
  toggleCurrentStatusOverlayHandler: () => {
    const next = !state.showDebugModal
    noteRemoteOverlayWsSet('status', next)
    setShowDebugModal(next)
  },
  toggleTimersOverlayHandler: () => {
    const next = !state.showTimersOverlay
    noteRemoteOverlayWsSet('timers', next)
    setShowTimersOverlay(next)
  },
  setTimersOverlayHandler: (visible: boolean) => {
    noteRemoteOverlayWsSet('timers', visible)
    setShowTimersOverlay(visible)
  },
  setCurrentStatusOverlayHandler: (visible: boolean) => {
    noteRemoteOverlayWsSet('status', visible)
    setShowDebugModal(visible)
  },
  audienceWebRtcSignalHandler: (payload) => {
    void audienceWebRtcPreviewSession.handleRemoteSignal(payload)
  },
  playerDiagnosticCommandHandler: (command) => {
    handlePlayerDiagnosticCommand(apiClientService, command)
  },
  playerRemoteCommandHandler: (command) => {
    handlePlayerRemoteCommand(apiClientService, command)
  }
})

watch(
  () => [state.showDebugModal, state.showTimersOverlay] as const,
  () => {
    void reportPlaybackHeartbeatWithProofOfPlay()
  }
)

let lowMemoryWatchStop: (() => void) | null = null
let lastReportedLowMemory: boolean | undefined

function startLowMemoryWatchIfNeeded() {
  if (lowMemoryWatchStop != null) { return }
  // Plugin IPC only exists in Tauri; plain Vite dev (browser) has no `invoke` → poll would warn every interval.
  if (!isTauriContext()) { return }
  import('tauri-plugin-device-capacity-api').then((mod) => {
    lowMemoryWatchStop = mod.watchLowMemory((isLow: boolean) => {
      if (lastReportedLowMemory !== isLow) {
        addPlayerLowMemorySentryBreadcrumb(isLow)
        lastReportedLowMemory = isLow
      }
      state.lowMemoryMode = isLow
      if (isLow) {
        flushHeartbeatQueue()
      }
    }, 5 * 60 * 1000)
  }).catch(() => {})
}

const registrationClearDirectoryGate = createRegistrationClearDirectoryGate({
  fetchRegistration: opts => apiClientService.fetchRegistration(opts),
  authenticationService,
  fetchRegistrationStatus: () => apiClientService.fetchRegistrationStatusWithStaleCredentialRecovery()
})

const websocketService = new WebSocketService({
  authenticationService,
  webSocketMessageProcessor,
  getAppVersion: getResolvedPlayerAppVersion,
  getWebVersion: getWebBundleVersion,
  directoryGate: registrationClearDirectoryGate
})

geolocationService.on(GeolocationServiceEvent.ERROR, (error) => {
  state.geolocationError = error
  if (error != null && typeof (error as GeolocationPositionError).code === 'number') {
    const e = error as GeolocationPositionError
    const c = e.code
    if (c === 1 || c === 2 || c === 3) {
      void websocketService.sendGeolocationClientError({
        code: c,
        message: typeof e.message === 'string' ? e.message : ''
      }).catch(() => {
        // offline / WS not ready — telemetry is best-effort
      })
    }
  }
})

const getPlayerRegistrationAndRegistrationStatus = async () => {
  let registration = await authenticationService.getRegistration()
  let justSetRegistrationWithTenant = false
  /**
   * Tauri (Android/desktop): set `capacityTier` before registration-status so UI and heartbeats have
   * a tier early. Uses `getCachedHardwareCapacity()` (retries + backoff on flaky IPC). Person-CV uses the
   * same `tier !== 'low'` gate as the motion pipeline (null allowed).
   */
  if (isTauriContext()) {
    try {
      const capEarly = await getCachedHardwareCapacity()
      if (capEarly) {
        state.capacityTier = capEarly.tier as 'low' | 'mid' | 'high'
        syncPlaybackExcludeVideoFilter()
        applySentryIdentityFromState()
      }
    }
    catch {
      // ignore — heartbeat path will retry
    }
  }
  /**
   * Unregistered (`tenantId` null):
   * - S1: Pairing persistence omits `webOta` (see AuthenticationService); storage must not be the OTA source of truth.
   * - Still always hit GET /registration here so Offered # / reload logic use the API + match WS after deploys.
   * - S2: `fetchPairingRegistrationWithDedupe` merges overlapping callers (bootstrap + connect + `PAIRING_REGISTRATION_REFETCH_INTERVAL_MS` poll).
   */
  const hadCachedPairingOnly = registration !== null && registration.tenantId === null
  if (registration === null || registration.tenantId === null) {
    const capacity = await getCachedHardwareCapacity()
    if (capacity) {
      state.capacityTier = capacity.tier as 'low' | 'mid' | 'high'
      syncPlaybackExcludeVideoFilter()
      applySentryIdentityFromState()
    }
    try {
      registration = await fetchPairingRegistrationWithDedupe(async () => {
        try {
          return await apiClientService.fetchRegistration(capacity ? { hardwareCapacity: capacity } : undefined)
        }
        catch (e) {
          if (e instanceof ApiClientServiceUnauthorizedError && e.code != null && PLAYER_AUTH_SOFT_RETRY_CODES.has(e.code)) {
            return await apiClientService.fetchRegistration(capacity ? { hardwareCapacity: capacity } : undefined)
          }
          throw e
        }
      })
    }
    catch (err) {
      if (hadCachedPairingOnly) {
        logger.warn({ err }, 'fetchRegistration failed; using cached pairing registration')
        registration = await authenticationService.getRegistration()
      }
      if (registration === null || (registration.tenantId === null && !hadCachedPairingOnly)) {
        if (err instanceof ApiClientServiceUnauthorizedError) {
          await applyRegistrationUnauthorizedPolicy(err, 'fetch_registration_bootstrap')
          state.registrationBootstrapError = registrationBootstrapErrorMessage(err)
          return
        }
        state.registrationBootstrapError = registrationBootstrapErrorMessage(err)
        logger.warn({ err }, 'fetchRegistration failed')
        return
      }
      if (hadCachedPairingOnly && registration.tenantId === null) {
        await hydrateCachedPairingRegistrationIfNeeded(registration)
      }
    }
    if (registration === null) {
      return
    }
    // Avoid overwriting with unregistered when a concurrent caller already consumed the assigned row and we got a new code (double-GET race).
    if (registration.tenantId === null) {
      const current = await authenticationService.getRegistration()
      if (current !== null && current.tenantId !== null) {
        registration = current
      }
    }
    await authenticationService.setRegistration(registration)
    if (registration.tenantId !== null) {
      resetPlayerAuthAmbiguousFailureStreak()
      lastRegistrationAppliedAt = Date.now()
      justSetRegistrationWithTenant = true
      startLowMemoryWatchIfNeeded()
    }
    if (registration.tenantId === null) {
      // When unregistered, server returns explicit webOta (either {..} or null); treat explicit null as authoritative so we revert.
      const raw = registration as { webOta?: IWebOtaUpdateEventPayload | null }
      if ('webOta' in raw && raw.webOta === null) { lastHandshakeWebOta = null }
      const regWebOta = raw.webOta
      state.offeredWebOtaVersion = regWebOta?.version ?? null
      void checkAndApplyWebOta(regWebOta)
      void reloadWebBrowserForNewWebOta(regWebOta)
      return
    }
  }
  startLowMemoryWatchIfNeeded()
  // If we just loaded registration with a tenant while already CONNECTED, we may be on
  // PlayersDirectoryDO (first connect had no tenantId). Reconnect so the next connection
  // goes to TenantDO and receives registration-status-update pushes (LUCIMARK-PLAYER-WS-TENANT).
  if (justSetRegistrationWithTenant && state.webSocketServiceState === WebSocketClientState.CONNECTED) {
    reconnectingToTenant = true
    await websocketService.stop()
  }
  try {
    const registrationStatus = await fetchRegistrationStatusSingleWithSoftRetry()
    resetPlayerAuthAmbiguousFailureStreak()
    await setRegistrationStatus(registrationStatus)
  }
  catch (err) {
    let authCleared = false
    if (err instanceof ApiClientServiceUnauthorizedError) {
      authCleared = (await applyRegistrationUnauthorizedPolicy(err, 'fetch_registration_status_bootstrap')) === 'cleared'
      if (authCleared) {
        state.registrationBootstrapError = registrationBootstrapErrorMessage(err)
      }
    }
    if (!authCleared) {
      const cachedRow = await dbService.getRegistrationStatusSnapshotRecord()
      if (cachedRow !== null) {
        logger.warn({ err }, 'fetchRegistrationStatus failed; applying cached registration snapshot')
        state.registrationBootstrapError = null
        await setRegistrationStatus(cachedRow.payload, {
          source: 'cached',
          snapshotSavedAtMs: cachedRow.savedAtMs
        })
      }
      else if (!(err instanceof ApiClientServiceUnauthorizedError)) {
        state.registrationBootstrapError = registrationBootstrapErrorMessage(err)
        logger.warn({ err }, 'fetchRegistrationStatus failed; no cached snapshot')
      }
      else {
        state.registrationBootstrapError = registrationBootstrapErrorMessage(err)
        logger.warn({ err }, 'fetchRegistrationStatus failed after auth error; no cached snapshot')
      }
    }
  }
}

/** Dedupes concurrent registration bootstrap (main.ts splash gate + first WS connect). */
let registrationBootstrapInflight: Promise<void> | null = null

function runRegistrationBootstrap(): Promise<void> {
  if (registrationBootstrapInflight) {
    return registrationBootstrapInflight
  }
  registrationBootstrapInflight = getPlayerRegistrationAndRegistrationStatus()
    .catch((err) => {
      if (!(err instanceof ApiClientServiceUnauthorizedError)) {
        logger.warn({ err }, 'registration bootstrap failed')
      }
    })
    .finally(() => {
      registrationBootstrapInflight = null
    })
  return registrationBootstrapInflight
}

/** Resolves after the first bootstrap attempt or when {@link timeoutMs} elapses (whichever comes first). */
function waitForInitialRegistrationBootstrap(timeoutMs: number): Promise<void> {
  return Promise.race([
    runRegistrationBootstrap(),
    new Promise<void>((resolve) => {
      setTimeout(resolve, timeoutMs)
    })
  ])
}

let registrationRefetchTimeoutId: ReturnType<typeof setTimeout> | null = null

function scheduleRegistrationRefetch() {
  if (registrationRefetchTimeoutId !== null) {
    clearTimeout(registrationRefetchTimeoutId)
    registrationRefetchTimeoutId = null
  }
  void (async () => {
    const reg = await authenticationService.getRegistration()
    const delay = (reg?.tenantId != null) ? REGISTRATION_REFETCH_INTERVAL_MS : PAIRING_REGISTRATION_REFETCH_INTERVAL_MS
    registrationRefetchTimeoutId = setTimeout(async () => {
      registrationRefetchTimeoutId = null
      try {
        await getPlayerRegistrationAndRegistrationStatus()
      }
      catch (err) {
        if (err instanceof ApiClientServiceUnauthorizedError) {
          void applyRegistrationUnauthorizedPolicy(err, 'periodic_registration_refetch')
        }
        else {
          console.warn('Periodic registration refetch failed:', err)
        }
      }
      if (state.webSocketServiceState === WebSocketClientState.CONNECTED) {
        scheduleRegistrationRefetch()
      }
    }, delay)
  })()
}

async function clearPersistedRegistrationAfterTenantUnregister(context: string): Promise<void> {
  state.pairingAfterUnregisterPending = true
  state.firstPlaybackFramePainted = false
  poolingService.resetUnregisteredPollBackoff()
  reportRegistrationClearedToSentry({
    reason: 'server_revoke',
    source: context.includes('ws') ? 'websocket' : 'http',
    context,
    code: 'tenant_unregister',
    httpStatus: 401
  })
  resetPlayerAuthAmbiguousFailureStreak()
  await authenticationService.setRegistration(null, {
    registrationBackup: registrationBackupDispositionForPersistedClear('server_revoke')
  })
  await setRegistrationStatus(null)
}

async function reconcileRegistrationAfterTenantUnregister(context: string): Promise<void> {
  try {
    await getPlayerRegistrationAndRegistrationStatus()
  }
  catch (err) {
    if (err instanceof ApiClientServiceUnauthorizedError) {
      void applyRegistrationUnauthorizedPolicy(err, context)
    }
    else {
      logger.warn({ err, context }, 'registration reconcile failed after tenant unregister')
    }
  }
}

/** Coalesces duplicate WS `player-unregistered` + close 3000 handlers in the same unregister. */
let tenantUnregisterHandling: Promise<void> | null = null

/**
 * Tenant signalled unregister (WS message or close 3000). Clear persisted tenant credentials
 * immediately (playback + tenant UI), then reconcile via directory GET /registration (pairing row).
 * The explicit unregister signal wins over any stale registration-status snapshot.
 */
async function handleTenantUnregisteredByServer(context: string): Promise<void> {
  if (tenantUnregisterHandling !== null) {
    return tenantUnregisterHandling
  }
  tenantUnregisterHandling = (async () => {
    try {
      const registration = await authenticationService.getRegistration()
      if (registration?.tenantId != null) {
        await clearPersistedRegistrationAfterTenantUnregister(context)
      }
      else if (!state.pairingAfterUnregisterPending) {
        state.pairingAfterUnregisterPending = true
      }
      await reconcileRegistrationAfterTenantUnregister(context)
    }
    finally {
      tenantUnregisterHandling = null
    }
  })()
  return tenantUnregisterHandling
}

websocketService.on(WebSocketServiceEvent.DISCONNECTED, ({ code, reason }) => {
  if (isTenantRegistrationDropWebSocketClose(code, reason)) {
    const context = reason.includes('tenant removed')
      ? 'tenant_removed_ws_close'
      : 'tenant_unregister_ws_close'
    void handleTenantUnregisteredByServer(context)
  }
})

websocketService.on(WebSocketServiceEvent.STATE_UPDATE, async (webSocketServiceState) => {
  state.webSocketServiceState = webSocketServiceState

  if (webSocketServiceState === WebSocketClientState.CONNECTED) {
    reconnectingToTenant = false
    // Before PoolingService’s debounced stop, omit `pooling=true` on registration-status (race with server-side fix).
    apiClientService.clearPoolingHttpQueryFlag()
    await runRegistrationBootstrap()
    // Second chance: Tauri uses handshake JWT for download; plain web uses registration-status only (handshake can lag and re-trigger mismatch).
    if (state.tenant != null && lastHandshakeWebOta !== null && (getPlatform() !== 'web' || isTauriContext())) {
      applyWebOtaIfAllowed(lastHandshakeWebOta, state.tenant.webOtaApplyTiming)
    }
    if (geolocationService.lastReportedPosition !== null) {
      void websocketService.sendPosition(geolocationService.lastReportedPosition)
    }
    scheduleRegistrationRefetch()
  }
  else {
    if (registrationRefetchTimeoutId !== null) {
      clearTimeout(registrationRefetchTimeoutId)
      registrationRefetchTimeoutId = null
    }
    reconnectingToTenant = false
  }
})

websocketService.on(WebSocketServiceEvent.RTT, (rtt) => {
  state.rtt = rtt
})

websocketService.on(WebSocketServiceEvent.UPDATE_AVAILABLE, ({ update, webOtaApplyTiming }) => {
  void playerUpdaterService.update(update, { webOtaApplyTiming })
})

// Handshake webOta: always store for API responses that omit `webOta`. Plain-web registered players keep Offered # from registration-status so a late WS auth does not overwrite with a stale handshake; Tauri/Android still apply handshake for native OTA download.
websocketService.on(WebSocketServiceEvent.WEB_OTA_AVAILABLE, ({ webOta, webOtaApplyTiming }) => {
  lastHandshakeWebOta = webOta ?? null
  const plainWeb = getPlatform() === 'web' && !isTauriContext()
  if (!plainWeb || state.tenant == null) {
    state.offeredWebOtaVersion = webOta?.version ?? null
    applyWebOtaIfAllowed(webOta, webOtaApplyTiming)
  }
})

const poolingService = new PoolingService({
  websocketService,
  authenticationService,
  apiClientService,
  getOfflineRegistrationStatusSnapshot: async () => {
    const row = await dbService.getRegistrationStatusSnapshotRecord()
    return row == null ? null : { payload: row.payload, savedAtMs: row.savedAtMs }
  }
})
poolingService.on(PoolingServiceEvent.RUNNING, async (running) => {
  state.poolingServiceRunning = running
  // setIsPooling is observability-only telemetry — swallow network errors so a
  // transient fetch failure does not surface as an unhandled rejection
  // (LUCIMARK-PLAYER-1F).
  try {
    await apiClientService.setIsPooling(running)
  }
  catch {
    // ignore — player continues polling regardless
  }
  logger.debug(`${running ? '🟩' : '🛑'} Pooling service ${running ? 'started' : 'stopped'}.`)
})

poolingService.on(PoolingServiceEvent.REGISTRATION_STATUS, async (emission) => {
  await setRegistrationStatus(emission.payload, {
    source: emission.source,
    snapshotSavedAtMs: emission.snapshotSavedAtMs
  })
})

if (import.meta.env.DEV) {
  registerE2eRegistrationStatusRefetch(async () => {
    const registrationStatus = await fetchRegistrationStatusSingleWithSoftRetry()
    await setRegistrationStatus(registrationStatus)
  })
}

geolocationService.subscribe(async position => websocketService.sendPosition(position))

/** Start WebSocket and geolocation after initPlatform() so x-player-platform is correct (e.g. android). Called from main.ts. */
function startPlayerConnectivity() {
  // Studio-managed brightness (fixed / schedule / sensor) keeps working offline after a reboot.
  void brightnessAutomation.restore()
  void websocketService.start()
  void geolocationService.start()
}

const toggleFullScreen = () => {
  // iOS Safari / Chrome on iOS often lack Fullscreen API — feature-detect (PLAYER-11).
  if (getFullscreenElement() == null) {
    void getPlatformInstance().requestFullscreen?.().catch(() => {})
    return
  }
  void exitWebFullscreen()
}

const debouncedAppStateChangeHandler = debounce(async (isActive: boolean) => {
  if (!isActive) {
    await reportOfflineWithSafeDelay()
  }
  else {
    pokeWeeklyAvailabilityGateAndResync()
    await getPlayerRegistrationAndRegistrationStatus()
  }
}, 1000)

let watchdogTimer: ReturnType<typeof setInterval> | null = null

async function startAndroidWatchdog(): Promise<void> {
  if (watchdogTimer) {
    clearInterval(watchdogTimer)
    watchdogTimer = null
  }
  await watchdogService.start()
  watchdogTimer = setInterval(() => {
    void watchdogService.ping()
    // Crash/OOM self-heal needs a durable last_foreground_at while the UI loop is alive (#1369).
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      void recordAndroidProcessHeartbeat()
    }
  }, 1000)
}

async function stopAndroidWatchdog(): Promise<void> {
  if (watchdogTimer) {
    clearInterval(watchdogTimer)
    watchdogTimer = null
  }
  await watchdogService.stop()
}

export const init = async (app: App) => {
  await initPlayerBootTelemetry()
  state.platform = getPlatform()
  await syncStateAppVersion()
  sentryService.init(app)
  if (!state.playerId) {
    state.playerId = await authenticationService.getPlayerId()
  }
  await resolveAndroidIdPrefixForHeartbeat()
  syncPlayerSentryContext(null)
  if (isPlayerDiagnosticsSamplingEnabled()) {
    initPlayerDiagnosticsWorker()
  }
  if (getPlatform() === 'android') {
    bootstrapAndroidViewportRotationFromWindow()
    applyScreenOrientationToNative(state.screenRotation)
    syncPlaybackAspectRatioFilter()
    await startAndroidWatchdog()
    // Listen for Rust watchdog timeout (no ping within threshold)
    import('@tauri-apps/api/event').then(({ listen }) => {
      listen('watchdog-timeout', () => {
        logger.warn('watchdog timeout: no ping received, reloading')
        performWatchdogRecoveryReload()
      }).catch(() => {})
    }).catch(() => {})
  }

  getPlatformInstance().app.onAppStateChange?.((isActive) => {
    if (getPlatform() === 'android') {
      if (isActive) {
        void startAndroidWatchdog()
      }
      else {
        markPlayerCleanShutdown()
        void stopAndroidWatchdog()
      }
    }
    debouncedAppStateChangeHandler(isActive)
  })

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', (ev) => {
      if (!ev.persisted) {
        markPlayerCleanShutdown()
      }
    })
  }

  if (getPlatform() === 'android') {
    void listenAndroidPowerSourceChange(() => debouncedReportPowerSourceHeartbeat())
  }
  else {
    void listenWebPowerSourceChange(() => debouncedReportPowerSourceHeartbeat())
  }

  if (getPlatform() === 'web') {
    window.addEventListener('pagehide', () => {
      void apiClientService.reportOfflineKeepalive()
    })
    let webVisibilityWasHidden = document.visibilityState === 'hidden'
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        void reportOfflineWithSafeDelay()
        webVisibilityWasHidden = true
      }
      else {
        if (webVisibilityWasHidden) {
          pokeWeeklyAvailabilityGateAndResync()
          debouncedReloadPlaybackAssets()
        }
        webVisibilityWasHidden = false
      }
    })
  }
}

/** Run after the Android Tauri bridge is up so splash prefs + native orientation match ScreenService / localStorage. */
export function syncAndroidScreenRotationToNativePreferences() {
  applyScreenOrientationToNative(screen.rotation)
}

/** True when weekly windows are configured and device-local “now” is outside all windows (blackout). */
const weeklyAvailabilityPlaybackSuppressed = computed(() => {
  void state.weeklyAvailabilityGateEpoch
  void state.availabilityWeeklySchedule
  const t = state.tenant
  return t != null && t.quotaExcess !== true && isWeeklyAvailabilitySuppressingPlaybackNow()
})

export const usePlayer = () => {
  return {
    init,
    state,
    weeklyAvailabilityPlaybackSuppressed,
    /** Platform fields injected into every widget's config. Spread last so they cannot be overridden by user-authored config tiers. */
    platformWidgetConfig,
    getApiClientService: () => apiClientService,
    /** Effective playback schedule root from materialization (legacy campaign assignment is a subtype). */
    getPlaybackScheduleRootEffective: playbackMaterializationService.getPlaybackScheduleRootEffective.bind(
      playbackMaterializationService
    ),
    getPlaybackAssets: () => playbackMaterializationService.assets,
    /** Slim playback plan at current instant; send to scheduler worker (pair with {@link state.queue}). */
    getWorkerPlaybackPlanNow: () => playbackMaterializationService.getWorkerPlaybackPlanNow(),
    /**
     * Rebuild queue after a full playlist loop so embedded `rotating` / `random` picks advance for the next cycle.
     * Called from the playback scheduler when the playlist wraps (web and Android use the same main-thread tick path).
     */
    refreshEmbeddedPicksAfterLoopWrap: playbackMaterializationService.refreshEmbeddedPicksAfterLoopWrap.bind(playbackMaterializationService),
    /**
     * Call when the visible playback frame advances or the playlist loop wraps so a deferred registration-status
     * schedule swap can apply without interrupting the frame in progress.
     */
    notifyDeferredPlaybackScheduleFrameBoundary: () => {
      void (async () => {
        await flushDeferredRegistrationPlaybackScheduleAfterFrameBoundary()
        await healFeedMaterializationDesyncIfNeeded()
      })()
    },
    registerRegistrationPlaybackResyncHandler,
    registerCacheRepairMediaRemountHandler,
    /** Sends playback heartbeat (uptime), optional storage used %, hardware capacity, overlay visibility, materialized-loop `segmentResolvedLoopSeconds` when the queue is non-empty, and when proofOfPlay is enabled optional assetPlays and playlistUrn. See docs/player-storage.md and docs/proof-of-play.md. */
    reportPlaybackHeartbeat: reportPlaybackHeartbeatWithProofOfPlay,
    /** Cold-path aspect telemetry (frame transition): Sentry rising-edge when vertical. */
    reportPlaybackAspectTelemetrySample,
    /** Flush playback heartbeat queue (e.g. on reconnect). Offline heartbeats are stored in IndexedDB and sent when back online. */
    flushHeartbeatQueue,
    toggleFullScreen,
    setShowDebugModal,
    toggleStatusOverlayFromLocalGesture,
    setWidgetRevealTiming,
    setScreenRotation: (rotation: TScreenRotation) => { screen.rotation = rotation },
    rotateScreenRight: screen.rotateScreenRight.bind(screen),
    rotateScreenLeft: screen.rotateScreenLeft.bind(screen),
    resetPlayer: playerUpdaterService.reset.bind(playerUpdaterService),
    startPlayerConnectivity,
    runRegistrationBootstrap,
    waitForInitialRegistrationBootstrap,
    scanCachedBinaryAssetsAndReloadPlayback,
    /**
     * Force `fetchAssets` / materialization reload without scanning IDB (incomplete campaign list #1574).
     * Runs immediately; coalesces while a reload is already in flight (pending-commit retries thrash).
     */
    reloadPlaybackAssetsForce: () => {
      requestPlaybackAssetReload()
    },
    /** True while {@link reloadPlaybackAssetsForce} / coalesce fetch is running (incomplete-list wait). */
    isPlaybackAssetReloadInFlight: () => playbackAssetReloadCoalesce.isInFlight()
  }
}

export default usePlayer
